// English translations
export const en = {
  common: {
    loading: "Loading...",
    error: "Something went wrong",
    retry: "Try Again",
    save: "Save",
    cancel: "Cancel",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    submit: "Submit",
    close: "Close",
    recommended: "Recommended",
    included: "Included",
    comingSoon: "Coming soon",
    drawing: "Drawing",
    photo: "Photo",
    style: "Style",
    size: "Size",
    material: "Material",
    color: "Color",
    redo: "Redo",
    free: "Free",
    toy: "Toy",
    character: "Character",
    confirmed: "Confirmed",
    complete: "Complete",
    leave: "Leave",
    stay: "Stay",
    loadingMagic: "Loading magic...",
    userMenu: "User menu",
    openMenu: "Open menu",
    goBack: "Go Back",
    processing: "Processing...",
    addAnotherImage: "Add another image",
    familiesCreated: "families have created toys",
    untitled: "Untitled",
    untitledStory: "Untitled Story",
    untitledAnimation: "Untitled Animation",
    fromPrefix: "from ",
    rial: "IRR",
    themeLight: "Switch to light mode",
    themeDark: "Switch to dark mode",
    generatingCharacter: "Generating your character...",
    buildingModel: "Building your 3D model...",
    wizardSteps: "Wizard steps",
    pageNavigation: "Page navigation",
    cover: "Cover",
    backCover: "Back cover",
    original: "Original",
    after: "After",
    before: "Before",
  },

  credits: {
    insufficientTitle: "Not enough credits",
    insufficientBody: "Buy more to keep creating.",
    insufficientAction: "Buy credits",
    balance: {
      chip: "{n} credits",
    },
    buy: {
      title: "Buy credits",
      subtitle: "Pick a pack to top up your account.",
      cta: "Buy",
      pending: "Processing…",
      empty: "No credit packs available right now.",
      error: "Purchase failed. Please try again.",
      success: {
        title: "Credits added!",
        body: "{n} credits are now in your account.",
      },
    },
  },

  subscription: {
    currentPlan: "Current plan",
    subscribeCta: "Subscribe",
    subscribing: "Redirecting to payment…",
    returning: "Returning from payment…",
    success: {
      title: "You're subscribed!",
      body: "You're now on the {plan} plan.",
    },
    error: "Subscription failed. Please try again.",
    account: {
      title: "Subscription",
      monthlyCredits: "{n} credits per month",
      nextGrant: "Current period ends: {date}",
      noSubscription: "You don't have an active subscription.",
      browsePlans: "Browse plans",
      cancelCta: "Cancel subscription",
      cancelling: "Cancelling…",
      cancelled: "Cancelled",
      cancelSuccess: "Subscription will end at the close of this period.",
      cancelError: "Could not cancel. Please try again.",
      pastDue: "Renewal due",
      renewCta: "Renew",
      renewHint: "Pay again to receive this month's credits.",
      resumeCta: "Keep subscription",
      resumeSuccess: "Cancellation was withdrawn.",
      resumeError: "Could not resume. Please try again.",
      cancelsOn: "Ends {date}",
    },
  },


  header: {
    nav: {
      home: "Home",
      gallery: "Gallery",
      howItWorks: "How It Works",
      pricing: "Pricing",
      about: "About",
      reviews: "Reviews",
      startCreating: "Start Creating",
      signIn: "Sign In",
      signOut: "Sign Out",
      account: "My Account",
      create: "Create",
      orders: "Orders",
    },
    brand: "Rubby",
    followOn: "Follow Rubby on {name}",
    mobileTagline: "Let's create something magical",
    switchTo: "Switch to {name}",
  },

  landing: {
    hero: {
      headline: "Turn Their Art Into a Keepsake They'll Hold Forever",
      subheadline: "Upload a drawing or photo. Preview a 3D toy in seconds. We craft and ship the real thing.",
      cta: "Upload a Drawing — Free Preview",
      ctaSubtext: "No account needed · 30 seconds · Free 3D preview",
      tagline: "Their Art. Made Real.",
      trust: {
        safety: "Child-Safe Materials",
        shipping: "Ships Worldwide",
        preview: "Preview Before You Pay",
        guarantee: "100% Happiness Guaranteed",
      },
      showcase: {
        title: "See the Transformation",
        pair1: "Drawing to Cartoon Toy",
        pair2: "Drawing to Realistic Toy",
        pair3: "Photo to 3D Figure",
        pair4: "Photo to Funko Style",
      },
      tabs: {
        drawings: "From Drawings",
        photos: "From Photos",
        storybooks: "Storybooks",
        drawingsDesc: "Upload any child's drawing and watch it transform into a custom 3D toy.",
        photosDesc: "Turn photos of pets, people, or characters into stunning collectible figurines.",
        storybooksDesc: "Transform their artwork into a fully illustrated storybook with AI narration.",
        drawingsCta: "Try With a Drawing",
        photosCta: "Try With a Photo",
        storybooksCta: "Create a Storybook",
      },
    },
    gallery: {
      title: "Real Art. Real Toys. Real Families.",
      subtitle: "Every keepsake started as a drawing on someone's fridge",
      tabs: {
        drawings: "Drawings → Toys",
        photos: "Photos → Figures",
        storybooks: "Drawings → Storybooks",
      },
      seeFullGallery: "See Full Gallery",
      cta: "Create Yours — Upload a Drawing",
      styles: {
        cartoon: "Cartoon Style",
        cartoonDesc: "Bold colors and playful proportions turn any sketch into a vibrant cartoon character.",
        realistic: "Realistic Style",
        realisticDesc: "High-detail figurines with lifelike textures and accurate proportions from any drawing.",
        actionFigure: "Action Figure",
        actionFigureDesc: "Poseable collectible figures with dynamic details -- capes, shields, and all.",
        lego: "LEGO Style",
        legoDesc: "Brick by brick, their imagination becomes a timeless buildable character.",
        anime: "Anime Style",
        animeDesc: "Expressive eyes, dynamic poses, and manga-inspired proportions from their original art.",
        pixar: "Pixar 3D",
        pixarDesc: "Smooth surfaces, warm expressions, and movie-quality character design from any photo.",
        funko: "Funko Pop",
        funkoDesc: "Join the world's most collectible vinyl style - big head, bigger personality!",
        chibi: "Chibi Figurine",
        chibiDesc: "Kawaii cuteness overload with adorable oversized heads and tiny bodies.",
        vinyl: "Vinyl Toy",
        vinylDesc: "Designer art toy vibes with sleek, gallery-worthy aesthetics.",
        legoPhoto: "LEGO Style",
        legoPhotoDesc: "Become a minifigure! The ultimate tribute to the world's favorite toy.",
        watercolor: "Watercolor",
        watercolorDesc: "Soft, dreamy brushstrokes bring their drawing to life in a gentle storybook world.",
        cartoonStory: "Cartoon",
        cartoonStoryDesc: "Bold outlines and vibrant colors turn their art into a fun animated adventure.",
        crayon: "Crayon",
        crayonDesc: "Warm, textured crayon strokes preserve the handmade charm in every illustration.",
        classic: "Classic",
        classicDesc: "Timeless storybook illustrations with rich detail and nostalgic warmth.",
        animeStory: "Anime",
        animeStoryDesc: "Expressive manga-inspired art brings their characters into a dynamic story world.",
      },
    },
    features: {
      title: "Crafted for Joy. Built for Trust.",
      subtitle: "Every detail designed with parents, safety, and real play in mind",
      kidAuthentic: {
        title: "Kid-Authentic AI",
        description: "Our AI preserves every quirk — three-legged dragons, lopsided crowns, and all. We enhance, never correct.",
      },
      preview: {
        title: "Preview Before We Print",
        description: "See an interactive 3D preview and make edits before production begins. You approve every detail.",
      },
      childSafe: {
        title: "Child-Safe Materials",
        description: "BPA-free PLA, non-toxic resin, CE-compliant. Every material independently tested for little hands.",
      },
      trackedDelivery: {
        title: "Tracked Delivery",
        description: "Standard 10-14 days, Express 5-7 days. Real-time tracking from our workshop to your doorstep.",
      },
      ecoFriendly: {
        title: "Eco-Friendly Options",
        description: "Plant-based biodegradable PLA and recycled packaging. Built for play, kind to the planet.",
      },
      happiness: {
        title: "Happiness Guaranteed",
        description: "Love it or we remake it free. No questions, no hoops, no hassle.",
      },
    },
    testimonials: {
      title: "Families Love Their Keepsakes",
      subtitle: "Join thousands of parents who turned fridge art into forever treasures",
      verifiedPurchase: "Verified Purchase",
      trustAnchor: "4.9/5 from 500+ families",
      familyCount: "from 200+ happy families",
      microReviews: [
        "My daughter screamed with joy! ⭐⭐⭐⭐⭐",
        "Better quality than I expected",
        "The preview was exactly what arrived",
        "Already ordering a second one",
        "Best birthday gift ever",
        "Took 6 days, perfectly packed",
        "My son thinks he's a real inventor now",
        "Worth every penny"
      ],
      readAllReviews: "Read All Reviews",
      items: {
        sarah: { name: "Sarah M.", text: "My daughter literally cried happy tears. She couldn't believe her dragon princess was real. It's now her most prized possession." },
        james: { name: "James K.", text: "Turned my son's drawing of our dog into a figure. Incredibly detailed — even has his little collar. Sits proudly on his nightstand." },
        emily: { name: "Emily R.", text: "Best gift I've ever given my grandkids. They designed characters together and now play with them daily. Pure joy." },
        maria: { name: "Maria L.", text: "Ordered three toys from my kids' drawings. The quality blew me away — they look exactly like what my children imagined. Already ordering more." },
      },
    },
    cta: {
      eyebrow: "READY?",
      title: "Their Imagination Deserves to Be Real",
      subtitle: "Upload a drawing in 30 seconds. Get a free 3D preview. No account needed.",
      button: "Start Creating",
      trustSignals: "🛡️ Child-Safe  |  🚚 Free Shipping 75+  |  💚 Happiness Guaranteed",
      handwritten: "Ready to start?",
      trustFreePreview: "Free preview",
      trustSecureCheckout: "Secure checkout",
      trustTrackedShipping: "Tracked shipping",
      trustHappinessGuarantee: "Happiness guarantee",
    },
    delivery: {
      title: "From Our Workshop to Your Door",
      subtitle: "Fast, tracked, and eco-conscious shipping — because the wait should be as exciting as the unboxing.",
      steps: {
        order: {
          title: "Order Placed",
          desc: "Your design is securely received and queued for crafting.",
        },
        craft: {
          title: "Handcrafted",
          desc: "Our makers bring your artwork to life with care and precision.",
        },
        pack: {
          title: "Carefully Packed",
          desc: "Eco-friendly packaging with bubble wrap to keep it safe.",
        },
        ship: {
          title: "Shipped & Tracked",
          desc: "A tracking link is sent the moment your toy hits the road.",
        },
      },
      perks: {
        fast: {
          title: "5–7 Day Delivery",
          desc: "US, UK & EU orders arrive within a week.",
        },
        worldwide: {
          title: "Worldwide Shipping",
          desc: "We deliver to over 120 countries.",
        },
        tracked: {
          title: "Fully Tracked",
          desc: "Real-time tracking from dispatch to doorstep.",
        },
      },
    },
  },

  howItWorks: {
    hero: {
      title: "From Drawing to Doorstep in 4 Steps",
      subtitle: "No tech skills needed. No account required to start.",
    },
    steps: {
      capture: {
        title: "Capture the Drawing",
        description: "Grab that precious artwork from the fridge, sketchbook, or tablet. Every crayon stroke, every wild idea -- we'll bring it to life exactly as they imagined it.",
        highlights: ["Any drawing works -- messy is welcome!", "Digital art or paper masterpieces", "Even napkin doodles become treasures"],
      },
      share: {
        title: "Upload in 30 Seconds",
        description: "Snap a photo or upload their creation in seconds. Our friendly interface guides you through -- no tech skills needed. Just pure excitement!",
        highlights: ["Quick 30-second upload", "Safe & secure storage", "See a preview instantly"],
      },
      magic: {
        title: "Watch It Come to Life",
        description: "Our AI studies every detail, filling gaps and adding dimension while keeping their unique style. Then our artisans 3D print it with care.",
        highlights: ["AI preserves their artistic vision", "Professional-grade 3D printing", "Review & request tweaks"],
      },
      unbox: {
        title: "Unbox Pure Joy",
        description: "That moment when the package arrives... when they realize their imagination is now REAL. These are the memories that last forever.",
        highlights: ["Arrives in 7-10 days", "Carefully packaged with love", "Track every step of the journey"],
      },
    },
    features: {
      title: "Why Families Choose Rubby",
      subtitle: "We obsess over every detail so you can focus on the smiles",
      items: ["100% child-safe, non-toxic materials", "Vibrant colors that won't fade", "Built tough for real play", "Happiness guaranteed or full refund", "Eco-friendly biodegradable options", "Multiple sizes to choose from"],
    },
    cta: {
      title: "Ready to Start Creating?",
      subtitle: "Their next masterpiece is waiting to become real. Start now and create memories that last a lifetime.",
      startButton: "Start Creating",
      pricingButton: "See Pricing",
    },
  },

  pricing: {
    hero: {
      title: "Choose Your Keepsake",
      subtitle: "Simple pricing. Premium quality. No hidden fees.",
    },
    packages: {
      free: "Free",
      perToy: "toy",
      mostPopular: "Most Popular",
      cta: { free: "Try It Free", starter: "Get Started", premium: "Start Creating", deluxe: "Go Deluxe" },
    },
    addOns: {
      title: "Customization Options",
      subtitle: "Customize your physical toy with these options",
      items: { extraSize: "Extra Size Options", displayCase: "Protective Display Case", rush: "Rush Processing (3-5 days)", additionalCopy: "Additional Copy" },
      categories: {
        size: "Size Options",
        material: "Material Options",
        color_plan: "Color Options",
        extra: "Extra Options",
      },
      included: "Included",
      discount: "10% discount",
    },
    guarantees: {
      items: {
        satisfaction: { title: "100% Satisfaction", description: "Love it or full refund" },
        shipping: { title: "Free Shipping", description: "On all orders over £75" },
        delivery: { title: "On-Time Delivery", description: "Or your money back" },
      },
    },
    shipping: {
      title: "Shipping & Timeline",
      options: { title: "Shipping Options", standard: "Standard Shipping: Free (10-14 days)", express: "Express Shipping: $15 (7-10 days)", rush: "Rush Shipping: $30 (5-7 days)" },
      timeline: { title: "Production Timeline", ai: "AI Processing: 24-48 hours", printing: "3D Printing: 2-4 days", painting: "Finishing & Packaging: 1-2 days", quality: "Quality Check: 1 day" },
    },
    pageCta: { title: "Ready to Start Creating?", subtitle: "Upload your drawing and see the 3D preview", button: "Upload Your Drawing" },
  },

  about: {
    hero: {
      title: "We Believe Every Drawing Deserves to Be Real",
      subtitle: "RUBBY turns your child's imagination into custom 3D-printed toys, personalised storybooks, and animated short films — all from a single drawing or photo.",
    },
    mission: {
      title: "Our Mission",
      text: "Childhood is fleeting. Those crayon masterpieces on the fridge? They fade, get lost, end up forgotten in attic boxes. We refuse to let that happen. Whether it becomes a toy your child can hold, a storybook with their name on the cover, or an animated film starring their drawing, RUBBY turns fleeting moments of creativity into things families keep forever.",
    },
    vision: {
      title: "Our Vision",
      text: "A world where every child's imagination is celebrated — where no drawing is \"just a drawing,\" and where creativity is something you can touch, read, watch, and treasure for a lifetime.",
    },
    values: {
      title: "What We Stand For",
      subtitle: "More than a company, we're a promise to every creative kid out there",
      items: {
        kids: { title: "Kids Come First", description: "Every decision — physical or digital — starts with one question: 'Will this make a child smile?' That's our north star." },
        imagination: { title: "Imagination Matters", description: "Every scribble has a story. We use AI to amplify a child's vision, never to replace it." },
        safety: { title: "Safety Always", description: "Child-safe materials for toys, age-appropriate content review for storybooks and films. Parents stay in control at every step." },
        planet: { title: "Planet-Conscious", description: "Eco-friendly biodegradable materials for physical toys, efficient cloud rendering for digital products. Their future matters." },
      },
    },
    journey: {
      title: "Our Journey So Far",
      subtitle: "From one parent's wish to a multi-product creative studio for families",
      items: {
        sketch: { event: "The First Sketch", description: "RUBBY began with a parent's wish: 'I want my daughter to hold her dragon drawing.'" },
        thousand: { event: "1,000 Dreams Made Real", description: "Our first thousand families experienced the joy of holding their child's art as a real toy." },
        ai: { event: "AI-Powered Precision", description: "Launched our 4K multi-view 3D pipeline that preserves every quirk of the original drawing." },
        storybook: { event: "Storybooks Arrive", description: "Introduced personalised picture books with AI illustration and bilingual EN/FA narration — your child as the hero." },
        animation: { event: "Animated Films Launch", description: "From a single drawing or photo to a directed mini-movie, complete with scenes, voices, and music." },
      },
    },
    promise: {
      text: "If they can draw it, you can hold it, read it, and watch it.",
    },
    cta: {
      title: "Pick Your Magic",
      subtitle: "Toys, storybooks, or animated films — three ways to turn your child's creativity into something they'll treasure.",
      button: "Start Creating",
    },
  },

  gallery: {
    hero: { badge: "Gallery", title: "Imagination Made", titleHighlight: "Tangible", subtitle: "Every toy here started as a drawing by a real kid. These are their stories." },
    createdBy: "Created by",
    loadMore: "Discover More Creations",
    categories: { cartoon: "Cartoon", action: "Action", plush: "Plush", realistic: "Realistic" },
    items: {
      dragon: { title: "Sir Scales the Brave", artist: "Emily, age 7", story: "Emily drew this dragon to protect her little brother from nightmares. Now he guards his nightstand!" },
      captain: { title: "Captain Stardust", artist: "Marcus, age 9", story: "Marcus invented this superhero who can fly through space and eat unlimited ice cream." },
      fluffy: { title: "Fluffy McHugface", artist: "Sofia, age 6", story: "Sofia's imaginary friend is now real! She takes him everywhere, even to school." },
      robot: { title: "Robo-Buddy 3000", artist: "Lucas, age 8", story: "Lucas wants to be an inventor. This is his first 'real' robot, straight from his sketchbook." },
      princess: { title: "Princess Sparklehorn", artist: "Ava, age 7", story: "A unicorn princess who grants wishes. Ava says she's already granted 47 wishes!" },
      flex: { title: "The Incredible Flex", artist: "Noah, age 10", story: "Noah's original superhero with stretchy powers. Already planning the sequel character!" },
    },
    stats: {
      dreams: { value: "10,000+", label: "Dreams Brought to Life" },
      countries: { value: "50+", label: "Countries Reached" },
      rating: { value: "4.9/5", label: "Happy Family Rating" },
    },
    cta: { title: "Your Child's Creation Could Be Here", subtitle: "Every masterpiece deserves to be more than a drawing. Upload today and join our gallery of imagination.", button: "Start Creating" },
  },

  faq: {
    hero: { badge: "Questions? We've Got Answers!", title: "Everything You", titleHighlight: "Want to Know", subtitle: "Real questions from real parents (and a few curious grandparents!)" },
    categories: {
      gettingStarted: {
        title: "Getting Started",
        questions: {
          messy: { q: "My kid's drawing is pretty messy. Will it still work?", a: "Absolutely! Messy is beautiful - that's the charm! Our AI is trained to understand children's art in all its wonderful chaos." },
          upload: { q: "How do I get the drawing to you?", a: "Super easy! Just snap a photo with your phone or scan it. Upload it through our website (takes about 30 seconds), pick your style, and you're done!" },
          preview: { q: "Can I see what it'll look like before I order?", a: "Yes! Within minutes, our AI creates a 3D preview so you can see the result in 3D. You can request tweaks until it's perfect." },
        },
      },
      pricing: {
        title: "Pricing & Payment",
        questions: {
          cost: { q: "How much does a custom toy cost?", a: "Toys start from £49 for a small single-colour figure. Most families choose our £79 full-colour package in our toughest material. Storybooks and animated films use a separate credit-based plan — see the Pricing page for all options." },
          hidden: { q: "Any hidden fees I should know about?", a: "Nope! We hate surprises. The price you see includes your toy, beautiful packaging, and shipping." },
          notLike: { q: "What if my kid doesn't like it?", a: "We offer a 100% happiness guarantee. If your child isn't over-the-moon excited, contact us within 30 days and we'll make it right." },
          payment: { q: "Can I pay with PayPal/Apple Pay?", a: "Yes! We accept all major credit cards, PayPal, Apple Pay, and Google Pay. Everything is secured through Stripe." },
        },
      },
      production: {
        title: "Production & Shipping",
        questions: {
          timeline: { q: "How long until my kid can hold their toy?", a: "Standard magic takes 10-14 days from when you approve the design. Can't wait? Our rush option gets it there in 5-7 days!" },
          shipping: { q: "Do you ship to my country?", a: "We ship the magic to over 50 countries worldwide! International orders typically arrive in 2-3 weeks." },
          tracking: { q: "Can I track my order?", a: "Of course! You'll get a tracking number the moment it ships, plus we have a dedicated order tracking page." },
          damaged: { q: "What if it arrives damaged?", a: "We package everything like we're shipping our own kids' toys. But if something happens, snap a photo and contact us within 7 days." },
        },
      },
      product: {
        title: "Product Details",
        questions: {
          materials: { q: "What are the toys made of? Is it safe?", a: "Safety is non-negotiable. We use PLA (plant-based, biodegradable plastic), PETG, or high-detail resin. All materials meet international toy safety standards." },
          play: { q: "Can my kid actually play with it?", a: "That's the whole point! Our standard PLA and PETG toys are built tough for play - dropping, tossing, adventures in the backyard." },
          sizes: { q: "What sizes can I get?", a: "We offer four sizes: Small (4-6 inches), Medium (6-8 inches), Large (8-12 inches), and custom sizes for special requests!" },
          copies: { q: "Can I order copies for grandparents?", a: "Best gift ever! Additional copies of the same design are 50% off. Grandma, Auntie, the cousins... everyone can have one." },
        },
      },
      customization: {
        title: "Customization",
        questions: {
          changes: { q: "Can I request changes to the 3D model?", a: "Absolutely! During the preview stage, you can request tweaks. Premium and Deluxe packages include unlimited revisions." },
          colors: { q: "Can I pick the colors?", a: "For full-color packages, we match the colors from the original drawing. For single-color options, you can choose from 20+ vibrant colors." },
          family: { q: "My kid drew our whole family. Can you make that?", a: "We love family portraits! Multiple characters can be combined into one scene or made as separate figures." },
        },
      },
      safety: {
        title: "Safety & Quality",
        questions: {
          toddler: { q: "Is this safe for my toddler?", a: "We take safety seriously. Our toys meet international safety standards, but we recommend them for ages 3+." },
          quality: { q: "How do you make sure quality is consistent?", a: "Every single toy goes through three quality checks: after printing, after finishing, and before shipping." },
          guarantee: { q: "What's your guarantee?", a: "Simple: Love it or we fix it. If you're not thrilled with your toy, we'll remake it for free or give you a full refund." },
        },
      },
    },
    support: {
      title: "Still Curious?",
      subtitle: "Our friendly team loves talking to families. No question is too small!",
      chat: { title: "Chat With Us", description: "Real humans, real-time help. Average response: 2 minutes!", button: "Start Chatting" },
      email: { title: "Email Us", description: "Prefer email? We'll get back to you within 24 hours", button: "Send Email" },
    },
  },

  contact: {
    hero: { badge: "We're Here For You", title: "Let's", titleHighlight: "Chat!", subtitle: "Questions about your order? Ideas for your next creation? Just want to share your kid's reaction? We'd love to hear from you!" },
    methods: {
      email: { title: "Email Us", description: "We reply within 24 hours - pinky promise!" },
      phone: { title: "Give Us a Call", description: "Real humans, Mon-Fri 9AM-6PM EST" },
      chat: { title: "Live Chat", value: "Chat Now", description: "Average wait: just 2 minutes!" },
      visit: { title: "Visit Our Workshop", description: "Tours by appointment -- see how we work!" },
    },
    form: {
      title: "Drop Us a Message",
      subtitle: "We read every single message personally!",
      name: { label: "Your Name", placeholder: "What should we call you?" },
      email: { label: "Email Address", placeholder: "your@email.com" },
      subject: { label: "What's This About?", placeholder: "Order question, new idea, feedback..." },
      message: { label: "Your Message", placeholder: "Tell us everything! We're great listeners..." },
      submit: "Send Message",
      submitting: "Sending Your Message...",
      success: { title: "Message Sent! 💌", description: "We'll get back to you within 24 hours. Thanks for reaching out!" },
      error: { title: "Oops!", description: "Something went wrong. Please try again or email us directly." },
      errors: {
        nameRequired: "Name is required",
        nameTooLong: "Name must be less than 100 characters",
        emailInvalid: "Invalid email address",
        emailTooLong: "Email must be less than 255 characters",
        subjectRequired: "Subject is required",
        subjectTooLong: "Subject must be less than 200 characters",
        messageTooShort: "Message must be at least 10 characters",
        messageTooLong: "Message must be less than 1000 characters",
      },
    },
    hours: { title: "When We're Around", weekdays: "Monday – Friday: 9:00 – 18:00 GMT", saturday: "Saturday: 10:00 – 16:00 GMT", sunday: "Sunday: We're recharging — email us and we'll reply on Monday" },
    quickAnswers: { title: "Quick Answers", faq: "Check our FAQ - we've answered tons of questions!", track: "Track your order status", howItWorks: "Learn how the process works", pricing: "See our pricing packages" },
    urgent: { title: "Urgent Order Issue?", description: "If something's wrong with your order, don't wait! Jump into live chat for immediate help.", button: "Start Live Chat Now" },
  },

  reviews: {
    hero: { badge: "Real Stories, Real Joy", title: "Loved by Families", titleHighlight: "Across the World", subtitle: "From cuddly toys to bedtime storybooks to animated short films — hear what families have made with RUBBY." },
    rating: { basedOn: "Based on {total} verified reviews", recommend: "97% of families recommend us!" },
    verified: "Verified",
    helpful: "{count} found helpful",
    loadMore: "Load More Stories",
    items: {
      sarah: { name: "Sarah M.", location: "New York, NY", title: "My daughter CRIED happy tears!", review: "I cannot express how incredible this moment was. My 7-year-old drew a unicorn princess months ago, and I secretly had Rubby turn it into a toy. When she opened it for her birthday, she literally couldn't believe her eyes.", date: "2 weeks ago" },
      michael: { name: "Michael R.", location: "Austin, TX", title: "Even better than I imagined", review: "My son drew our golden retriever with typical 6-year-old proportions (giant head, tiny legs). I wasn't sure how it would translate, but Rubby NAILED it.", date: "1 month ago" },
      emily: { name: "Emily L.", location: "Los Angeles, CA", title: "Customer service is exceptional too!", review: "My daughter drew a superhero and we wanted a small tweak to the cape. The team was SO patient with our requests - we went through 3 revisions.", date: "3 weeks ago" },
      david: { name: "David K.", location: "Chicago, IL", title: "Great concept, solid execution", review: "Turned my daughter's crayon family portrait into a figure. The AI did an amazing job interpreting her... creative... anatomy choices for all of us.", date: "1 week ago" },
      jennifer: { name: "Jennifer P.", location: "Miami, FL", title: "Changed my nephew's life", review: "My nephew is autistic and drawing is his way of communicating. He creates incredible detailed robots. Getting one of his drawings turned into a real toy was TRANSFORMATIVE for him.", date: "2 months ago" },
      robert: { name: "Robert S.", location: "Seattle, WA", title: "Engineer-approved quality", review: "I'm an engineer, so I'm naturally skeptical and picky about manufacturing quality. This toy exceeded my expectations.", date: "3 weeks ago" },
    },
    cta: { title: "Ready to Create Your Own Story?", subtitle: "Join thousands of families who turned fridge art into forever treasures", button: "Start Creating" },
  },

  footer: {
    brand: "Rubby",
    tagline: "From Fridge to Forever",
    create: { title: "Create", fromDrawing: "From a Drawing", fromPhoto: "From a Photo", storybook: "Create a Storybook", giftCards: "Gift Cards" },
    learnMore: { title: "Learn More", howItWorks: "How It Works", gallery: "Gallery", pricing: "Pricing", qualitySafety: "Quality & Safety" },
    support: { title: "Support", faq: "FAQ", contact: "Contact Us", track: "Track My Order", reviews: "Reviews" },
    copyright: "© 2026 Rubby. All rights reserved.",
    legal: { terms: "Terms", privacy: "Privacy", cookies: "Cookies" },
    newsletter: { title: "Get creative inspiration", button: "Sign up", emailPlaceholder: "Your email" },
  },

  terms: {
    badge: "Legal Stuff",
    title: "Terms of Service",
    subtitle: "The boring (but important) details about using Rubby",
    lastUpdated: "Last updated",
    sections: {
      welcome: { title: "1. Welcome to Rubby!", content: "By using Rubby, you agree to these Terms of Service. We know legal documents aren't fun to read, but these terms help protect both you and us." },
      whatWeDo: { title: "2. What We Do", content: "RUBBY transforms drawings and photos into three kinds of keepsakes: custom 3D-printed toys, personalised AI-illustrated storybooks (with optional bilingual narration), and short animated films. When you upload content and place an order or spend credits, you are commissioning us to create that specific physical product or digital deliverable." },
      yourArtwork: { title: "3. Your Artwork, Your Rights", ownership: "You keep ownership of any original artwork you upload. By uploading, you grant us permission to use that artwork solely for:", items: ["Creating your custom toy", "Showing you previews during the creation process", "With your permission, featuring your creation in our gallery"], important: "Important: Only upload artwork you have the right to use. Don't upload copyrighted characters unless you own the rights." },
      models: { title: "4. Our 3D Models", content: "The 3D models we create are a collaborative work. You own your original concept, and we retain rights to the 3D model itself." },
      orders: { title: "5. Orders & Payment", pricing: { label: "Pricing:", content: "Prices are clearly displayed before you order. No hidden fees." }, payment: { label: "Payment:", content: "Full payment is required before production begins." }, custom: { label: "Custom nature:", content: "Each toy is made-to-order specifically for you." }, approval: { label: "Approval:", content: "You approve the 3D preview before we print." } },
      revisions: { title: "6. Revisions & Changes", content: "During the preview stage, you can request changes. After you approve and we begin printing, changes aren't possible." },
      guarantee: { title: "7. Our Happiness Guarantee", intro: "We want you to be thrilled with your toy. If something's wrong:", items: ["Manufacturing defects: Free replacement, no questions asked", "Shipping damage: Free replacement with photo proof within 7 days", "Not satisfied: Contact us within 30 days for a refund or remake"], note: "We can't offer refunds for approved designs you later change your mind about." },
      shipping: { title: "8. Shipping & Delivery", items: ["Estimated delivery times are provided after order approval", "We ship to 50+ countries worldwide", "Once shipped, tracking information is provided", "Delivery delays due to customs are outside our control"] },
      safety: { title: "9. Safety & Age Appropriateness", content: "Our toys are designed for children ages 3 and up. Some detailed designs may have small parts." },
      account: { title: "10. Account Responsibility", content: "You're responsible for maintaining the confidentiality of your account. Please use a strong password." },
      liability: { title: "11. Limitation of Liability", content: "We create toys, not miracles. Our maximum liability is limited to the amount you paid for your order." },
      changes: { title: "12. Changes to These Terms", content: "We may update these terms occasionally. We'll notify you of significant changes via email." },
      contact: { title: "13. Contact Us", intro: "Questions about these terms?", items: ["Email: legal@rubby.co.uk", "Or just use our regular contact form"] },
    },
    footer: "Thank you for being part of the Rubby family. Now go start creating! ✨",
  },

  privacy: {
    badge: "Your Privacy Matters",
    title: "Privacy Policy",
    subtitle: "How we protect your family's data (spoiler: we take it seriously!)",
    lastUpdated: "Last updated",
    sections: {
      intro: { title: "1. We're Parents Too", content: "At Rubby, many of us are parents. We understand the importance of protecting your family's privacy." },
      collect: {
        title: "2. What We Collect",
        provided: {
          title: "Information You Provide:",
          account: { label: "Account info:", content: "Name, email address, password" },
          shipping: { label: "Shipping info:", content: "Address, phone number (for delivery)" },
          payment: { label: "Payment info:", content: "Payment details processed securely by Zarinpal" },
          artwork: { label: "Your artwork:", content: "The drawings and photos you upload" },
          communications: { label: "Communications:", content: "Messages you send us" },
        },
        automatic: { title: "Information We Collect Automatically:", items: ["Device type and browser", "IP address and general location", "How you use our website", "Cookies for keeping you logged in"] },
      },
      use: { title: "3. How We Use Your Information", items: { create: { label: "Create your toy:", content: "This is the main reason!" }, communicate: { label: "Communicate with you:", content: "Order updates and support" }, improve: { label: "Improve our service:", content: "Understanding how families use Rubby" }, prevent: { label: "Prevent fraud:", content: "Keeping everyone safe" } } },
      artwork: { title: "4. Your Artwork Privacy", important: "This is important:", items: ["We only use uploaded artwork to create YOUR order", "We never sell artwork or use it for training AI without permission", "Gallery sharing is 100% opt-in", "You can request deletion at any time"] },
      share: { title: "5. Who We Share With", intro: "We don't sell your data. We only share with:", items: { payment: { label: "Payment processor (Zarinpal):", content: "To process your payment" }, shipping: { label: "Shipping carriers:", content: "Your shipping address for delivery" }, cloud: { label: "Cloud services:", content: "To store your data securely" }, legal: { label: "Legal requirements:", content: "Only if legally required" } } },
      security: { title: "6. Keeping Your Data Safe", intro: "We use industry-standard security measures:", items: ["SSL/TLS encryption for all data in transit", "Encrypted storage for sensitive data", "Regular security audits", "Limited employee access", "Secure data centers"] },
      children: { title: "7. Children's Privacy (COPPA)", content: "Our service is for parents/guardians. We don't knowingly collect personal information from children under 13." },
      rights: { title: "8. Your Rights", intro: "You have the right to:", items: { access: { label: "Access:", content: "Request a copy of your data" }, correct: { label: "Correct:", content: "Update any inaccurate information" }, delete: { label: "Delete:", content: "Request deletion of your account" }, export: { label: "Export:", content: "Get your data in a portable format" }, optout: { label: "Opt-out:", content: "Unsubscribe from marketing emails" } }, exercise: "To exercise these rights, email privacy@rubby.co.uk. Under UK-GDPR and EU-GDPR we will respond within 30 days." },
      cookies: { title: "9. Cookies", intro: "We use cookies for:", items: { essential: { label: "Essential:", content: "Keeping you logged in" }, analytics: { label: "Analytics:", content: "Understanding site usage" }, preferences: { label: "Preferences:", content: "Remembering your settings" } }, disable: "You can disable cookies in your browser." },
      international: { title: "10. International Transfers", content: "RUBBY is operated from the United Kingdom. Your data is processed in the UK and EU under UK-GDPR and EU-GDPR safeguards. Where we use sub-processors based outside the UK/EU (for example AI providers or cloud infrastructure in the US), transfers are protected by Standard Contractual Clauses or equivalent approved mechanisms." },
      retention: { title: "11. Data Retention", intro: "We keep your data for:", items: { account: { label: "Account data:", content: "As long as your account is active" }, orders: { label: "Order history:", content: "7 years (for legal/tax purposes)" }, artwork: { label: "Uploaded artwork:", content: "Until you request deletion" }, analytics: { label: "Analytics data:", content: "26 months in anonymized form" } } },
      changes: { title: "12. Changes to This Policy", content: "We may update this policy occasionally. We'll notify you of significant changes via email." },
      contact: { title: "13. Questions?", intro: "If you have ANY questions about how we handle your data:", items: ["Email: privacy@rubby.co.uk", "Or reach out through our contact page"] },
    },
    footer: "Your trust means everything to us. Thank you for letting us be part of your family's creative journey. 💜",
  },

  account: {
    title: "My Account",
    subtitle: "Manage your profile, orders, and projects",
    tabs: { profile: "Profile", kids: "My Kids", shipping: "Shipping", orders: "Orders", projects: "Projects" },
    profile: { 
      title: "Profile Details", 
      description: "Update your personal information",
      fullName: "Full Name", 
      fullNamePlaceholder: "Enter your full name",
      firstName: "First Name",
      firstNamePlaceholder: "Enter your first name",
      lastName: "Last Name",
      lastNamePlaceholder: "Enter your last name",
      email: "Email", 
      emailDisabled: "Email cannot be changed",
      phone: "Phone Number", 
      phonePlaceholder: "Enter your phone number",
      noName: "No name set",
      save: "Save Profile",
      dob: "Date of Birth",
      dobPlaceholder: "Pick your date of birth",
      language: "Preferred Language",
      languageEn: "English",
      languageFa: "Persian (فارسی)",
    },
    kids: {
      title: "My Kids",
      description: "Manage your children's profiles for personalized experiences",
      addChild: "Add Child",
      editChild: "Edit",
      deleteChild: "Delete",
      empty: "No kids added yet",
      emptyDesc: "Add your children's profiles so we can personalize toys and stories for them!",
      firstName: "First Name",
      firstNamePlaceholder: "Child's first name",
      lastName: "Last Name",
      lastNamePlaceholder: "Child's last name (optional)",
      dob: "Date of Birth",
      dobPlaceholder: "Pick date of birth",
      gender: "Gender",
      genderBoy: "Boy",
      genderGirl: "Girl",
      genderOther: "Other",
      genderPreferNot: "Prefer not to say",
      interests: "Favorite Interests",
      interestsPlaceholder: "Select interests",
      avatarEmoji: "Pick an Emoji",
      save: "Save Child",
      cancel: "Cancel",
      confirmDelete: "Are you sure you want to remove this child's profile?",
      childSaved: "Child profile saved!",
      childDeleted: "Child profile removed",
      childError: "Failed to save child profile",
      deleteError: "Failed to remove child profile",
      age: "{years} years old",
      interestOptions: {
        animals: "Animals",
        dinosaurs: "Dinosaurs",
        space: "Space",
        princesses: "Princesses",
        superheroes: "Superheroes",
        cars: "Cars",
        art: "Art",
        music: "Music",
        sports: "Sports",
        science: "Science",
      },
    },
    shipping: { 
      title: "Default Shipping Address", 
      description: "This address will be pre-filled at checkout",
      address: "Street Address", 
      addressPlaceholder: "123 Main Street",
      city: "City", 
      cityPlaceholder: "City",
      state: "State / Province", 
      statePlaceholder: "State",
      postalCode: "Postal Code", 
      postalCodePlaceholder: "12345",
      country: "Country", 
      countryPlaceholder: "Country",
      save: "Save Address" 
    },
    orders: { 
      title: "My Orders", 
      description: "Track your toy orders and shipments",
      empty: "No orders yet", 
      emptyDesc: "Your orders will appear here once you complete a purchase",
      startCreating: "Start Creating",
      tracking: "Tracking",
      status: "Status", 
      date: "Order Date", 
      viewDetails: "View Details",
      reorder: "Reorder",
      reorderBanner: "Creating a reorder. Previous specifications have been loaded."
    },
    projects: { 
      title: "My Projects", 
      description: "View and continue working on your toy designs",
      empty: "No projects yet", 
      emptyDesc: "Start creating your first custom toy",
      createToy: "Create a Toy",
      newProject: "New Project", 
      continue: "Click to continue →", 
      status: "Status",
      drawingToToy: "Drawing to Toy",
      photoToToy: "Photo to Toy",
      drawingToStory: "Drawing to Story",
      newDrawingToy: "New Drawing",
      newPhotoToy: "New Photo",
      newStorybook: "New Story",
      emptyDrawings: "No drawing projects yet",
      emptyPhotos: "No photo projects yet",
      emptyStorybooks: "No storybook projects yet",
      toy: "Toy",
      story: "Story",
      animation: "Animation",
      game: "Game",
      newToy: "New Toy",
      newAnimation: "New Animation",
      emptyToys: "No toy projects yet",
      emptyAnimations: "No animation projects yet",
      gameComingSoon: "Games are coming soon",
    },
    toasts: {
      profileUpdated: "Profile updated successfully",
      profileError: "Failed to save profile",
      addressSaved: "Shipping address saved",
      addressError: "Failed to save address",
      loadProfileError: "Failed to load profile",
      loadOrdersError: "Failed to load orders",
      loadProjectsError: "Failed to load projects"
    }
  },

  wizard: {
    // Page header
    pageTitle: "Create Your Toy",
    pageSubtitle: "Follow these steps to transform your drawing into an amazing 3D toy",
    pageSubtitlePhoto: "Follow these steps to transform your photo into an amazing 3D figure",
    progress: "Progress",
    stepOf: "Step {current} of {total}",
    stepsTitle: "Steps",
    
    // Step names
    steps: { 
      upload: "Show Riko", 
      uploadDesc: "Upload a drawing or photo for Riko to work magic on",
      preview: "The Magic", 
      previewDesc: "Watch Riko turn it into a character",
      rotation: "All Around",
      rotationDesc: "See your character from every angle",
      model: "In 3D", 
      modelDesc: "Meet your creation in 3D — then choose what to make",
      specs: "Make it Yours", 
      specsDesc: "Pick size, material, and colour",
      checkout: "Checkout", 
      checkoutDesc: "Confirm and pay",
      confirmation: "Hooray!",
      confirmationDesc: "Your creation is ready to share"
    },
    
    // New Project Dialog
    newProject: {
      button: "New Project",
      title: "Start a New Project?",
      description: "Your current project will be saved and you can continue it later from your Account page.",
      cancel: "Cancel",
      confirm: "Start New Project"
    },

    leave: {
      title: "Leave wizard?",
      description: "Your progress will be saved. You can continue later.",
      stay: "Stay",
      leave: "Leave",
    },
    
    // Order locked banner
    orderLocked: {
      title: "Order Confirmed",
      description: "This order has been confirmed. These settings cannot be changed."
    },

    recovery: {
      title: "This step seems stuck",
      titleFailed: "This step failed",
      body: "You can retry here, or go to the next step while we restart generation.",
      retry: "Retry",
      goNext: "Go next",
      continuing: "Continuing…",
      waitingPrevious: "Previous generation is still running…",
      alreadyRunning: "Generation is already running",
    },

    catalog: {
      loading: "Loading prices…",
      errorTitle: "Couldn't load prices",
      errorBody: "Pricing isn't available yet. Retry to continue.",
      retry: "Retry",
      notReady: "Prices aren't ready yet. Please wait or retry.",
    },
    
    // Step 0: Upload
    upload: { 
      title: "Let's Create Magic!",
      subtitleDrawing: "Choose a style, upload your artwork, and name your creation",
      subtitlePhoto: "Choose a style, upload your photo, and name your creation",
      chooseStyle: "Choose Your Style",
      toyName: "Name Your Creation",
      toyNamePlaceholder: "Give your toy a memorable name...",
      toyNameError: "Toy name must be at least 3 characters",
      toyNameErrorMax: "Toy name must be less than 50 characters",
      toyNameErrorFormat: "Only letters, numbers, spaces, hyphens and underscores allowed",
      uploadTitle: "Upload Your Image",
      analysing: "Analysing your image…",
      analyseFailed: "Could not analyse the image. Please try again.",
      pickCharacterTitle: "Pick a character",
      pickCharacterHint: "We found these characters. Choose the one you'd like to turn into a toy.",
      pickCharacterHintStory:
        "We found these characters. Select the ones you want in your story.",
      pickCharacterHintAnimation:
        "We found these characters. Select the ones you want in your animation.",
      nameYourCharacter: "Name your character",
      confirmCharacter: "Use this character",
      noCharactersFound: "We couldn't find a character in this image. Try another one.",
      drawingOrPhoto: "Is this a drawing or a photo?",
      styleTips: "Tips for a great style",
      styleTipsTitle: "Style Tips",
      styleTip1: "Use a clear photo with good lighting.",
      styleTip2: "Make sure the face and features are visible.",
      styleTip3: "Avoid busy backgrounds.",
      styleTip4: "Pick a cartoon style for a more natural look.",
      smartRecs: "Smart recommendations applied",
      pickStyleConfirm: "Pick a style and confirm a character for every uploaded image.",
      uploadThenAnalyse: "Upload one or more images, then click Analyse on each to find characters.",
      animationStyle: "Animation style",
      styles: { 
        cartoon: "Cartoon", 
        realistic: "Realistic", 
        anime: "Anime", 
        lego: "LEGO", 
        actionFigure: "Action Figure",
        pixar: "Pixar 3D",
        funko: "Funko Pop",
        chibi: "Chibi Figurine",
        vinyl: "Vinyl Toy"
      }
    },
    
    // Step 1: Magic Preview
    preview: { 
      title: "AI Character Preview",
      originalLabel: "Original",
      resultLabel: "Generated Character",
      characterReady: "Your 2D character is ready!",
      characterSuccess: "Character generated successfully! Click Next to create your 3D model.",
      generating: "is generating...",
      generatingDefault: "Generating character...",
      pleaseWait: "This may take a few minutes. Please wait...",
      requestEdit: "Edit",
      processing: "Processing...",
      editingOverlay: "Editing…",
      editLabel: "Describe the changes you'd like",
      editPlaceholder: "Example: Make the eyes bigger, change hair color to blue, add a smile...",
      editMinLength: "Please describe the changes (at least 10 characters)",
      editMaxLength: "Description must be less than 500 characters",
      processEdit: "Submit Edit",
      regenerate: "Regenerate",
      retryLoading: "Retry Loading Image",
      versionsTitle: "Previous versions",
      versionsEmpty: "No saved versions yet",
      versionBase: "Original",
      versionLabel: "Version {n}",
      versionActive: "Selected",
      versionSelect: "Use this version",
      revert: "Revert",
      regenerateUnavailable: "Regenerate is not available until the project is created.",
    },
    
    // Step 2: 180° View
    rotation: {
      title: "180° Character View",
      subtitle: "AI-generated back view of your character for better 3D quality",
      generating: "Generating 180° view...",
      generatingDesc: "Creating the back view of your character...",
      confirmingDesc: "Preparing rotation image...",
      ready: "180° view is ready!",
      readyDesc: "Confirm or regenerate the back view before proceeding to 3D generation.",
      frontView: "Front View",
      backView: "Back View",
      frontLabel: "Front View",
      backLabel: "Back View (Generated)",
      regenerate: "Regenerate",
      confirm: "Confirm & Continue",
      failed: "Failed to generate 180° view. Please try again.",
      failedDesc: "Please try again.",
      bgRemovalFailed: "Background removal on rotation image failed. You can regenerate or try again.",
      retry: "Retry",
      imageUnavailable: "This reference sheet could not be loaded. Refresh the page or regenerate it."
    },

    // Step 3: 3D Model
    model: { 
      title: "AI 3D Model",
      modelReady: "Your 3D model is ready!",
      modelSuccess: "3D model generated successfully! You can proceed to the next step.",
      waitingForUrl: "Waiting for model_3d_url to be populated...",
      manualUpload: "3D model is ready - please upload it manually to the database",
      generating: "Generating 3D model...",
      processingToy: "Processing \"{name}\"...",
      processingDefault: "Processing...",
      creatingModel: "Creating your 3D model (~90 seconds)...",
      format: "Format",
      readyForProduction: "Ready for production"
    },
    
    // Step 3: Specs
    specs: { 
      title: "Production Specifications",
      subtitle: "Configure your custom toy specifications for production",
      basePrice: "$79.99",
      size: { 
        title: "Size",
        small: "Small (10 cm)",
        smallDesc: "Perfect for desk display",
        medium: "Medium (15 cm)",
        mediumDesc: "Most popular size",
        large: "Large (20 cm)",
        largeDesc: "Great for gifts",
        veryLarge: "Very Large (25 cm)",
        veryLargeDesc: "Premium display piece"
      }, 
      material: { 
        title: "Material",
        pla: "Standard PLA",
        plaDesc: "Durable, eco-friendly plastic",
        petg: "Tough PETG",
        petgDesc: "Extra strong and impact resistant",
        tpu: "Flexible TPU",
        tpuDesc: "Soft, bendable material",
        resin: "High-Detail Resin",
        resinDesc: "Ultra-fine details and smooth finish"
      }, 
      color: { 
        title: "Color Plan",
        single: "Single Color",
        singleDesc: "One solid color",
        full: "Full Color",
        fullDesc: "Multi-color printing"
      },
      totalPrice: "Total Price:",
      priceIncludes: "Includes production, materials, and free UK shipping"
    },
    
    // Step 4: Checkout
    checkout: { 
      title: "Checkout",
      subtitle: "Complete your order and payment",
      orderSummary: "Order Summary",
      basePrice: "Base Price (3D Toy)",
      size: "Size",
      material: "Material",
      colorPlan: "Color Plan",
      total: "Total",
      prototypeMode: "Prototype Mode",
      prototypeDesc: "This is a demonstration. No real payment will be processed.",
      payButton: "Continue with Mock Payment",
      payButtonProcessing: "Processing...",
      prototypeNote: "Prototype mode - order will be confirmed without actual payment",
      lockedTotal: "Locked order total:",
      missingQuote: "A locked quote is required before checkout. Go back and continue to refresh pricing.",
    },
    
    // Step 5: Confirmation
    confirmation: { 
      title: "Order Confirmed!",
      subtitle: "is now in production",
      orderNumber: "Order #",
      trackOrder: "Track Your Order",
      viewModel: "View 3D model"
    },

    // Fulfillment chooser on the "In 3D" step
    fulfillment: {
      title: "How do you want it?",
      subtitle: "Pick what you're taking home today",
      required: "Pick an option to continue",
      payModel: "Pay {price} for digital 3D model",
      downloadModel: "Download 3D Model (.glb)",
      downloadWebModel: "Download web version (.glb)",
      modelPreparing: "Your model is being prepared — refresh in a moment",
      options: {
        model: {
          title: "3D Model",
          desc: "Instant .glb download — print it yourself or use it in games"
        },
        toy: {
          title: "Physical Toy",
          desc: "We print, paint and ship it to you"
        },
        both: {
          title: "Both",
          desc: "Digital model + physical toy"
        }
      },
      checkoutHint: {
        model: "Digital 3D model — instant download after payment",
        toy: "Physical toy — printed and shipped to your door",
        both: "Physical toy + digital 3D model download"
      },
      confirmationSubtitle: {
        model: "is ready to download",
        toy: "is now in production",
        both: "is ready to download and in production"
      }
    },

    
    // Navigation
    nav: {
      back: "Back",
      next: "Next",
      finish: "Finish",
      creatingProject: "Creating Project...",
      starting3D: "Starting 3D Generation...",
      processingEdit: "Processing Edit...",
      backToHooray: "Back to Hooray!"
    },
    
    // Toasts
    toasts: {
      fileUploaded: "File uploaded successfully",
      fileUploadedDesc: "{fileName} is ready for processing.",
      startingFresh: "Starting fresh",
      startingFreshDesc: "New image uploaded. Previous progress has been reset.",
      specsUpdated: "Specifications updated",
      specsUpdatedDesc: "Price has been recalculated.",
      specsSaved: "Specifications saved!",
      specsSavedDesc: "Your toy specifications have been confirmed.",
      specsErrorDesc: "Failed to save specifications. Please try again.",
      validationFailed: "Validation failed",
      validationFailedDesc: "Please check your inputs.",
      imageRequired: "Image required",
      uploadDrawingRequired: "Please upload a drawing to continue.",
      uploadPhotoRequired: "Please upload a photo to continue.",
      authRequired: "Authentication required",
      authRequiredDesc: "Please log in to continue.",
      changesDetected: "Changes detected",
      changesDetectedDesc: "Regenerating your character with the new settings...",
      projectCreated: "Project created!",
      generatingCharacter: "Now generating your character...",
      generatingFigure: "Now generating your figure...",
      errorCreatingProject: "Error creating project",
      errorCreatingProjectDesc: "Failed to save your project. Please try again.",
      errorUpdatingProject: "Error updating project",
      errorUpdatingProjectDesc: "Failed to update your project. Please try again.",
      creatingNewVersion: "Creating new version",
      creatingNewVersionDesc: "New edit requested. 3D model will be regenerated.",
      processingChanges: "Processing changes...",
      processingChangesDesc: "AI is editing your 2D character. This may take a moment.",
      changesApplied: "Changes applied!",
      changesAppliedDesc: "Your 2D character has been updated successfully.",
      editErrorDesc: "Failed to process changes. Please try again.",
      preparingImage: "Preparing Image",
      preparingImageDesc: "Removing background from your character...",
      backgroundRemoved: "Background Removed",
      backgroundRemovedDesc: "Starting 3D model generation...",
      generationStarted: "3D Generation Started",
      generationStartedDesc: "Creating your 3D model (~90 seconds)...",
      generationFailed: "Generation Failed",
      generationFailedDesc: "Failed to generate 3D model. Please try again.",
      modelGenerated: "3D Model generated!",
      modelGeneratedDesc: "Your 3D model is ready.",
      characterGenerated: "Character generated!",
      characterGeneratedDesc: "Your 2D character is ready.",
      paymentComplete: "Prototype Payment Complete! 🎉",
      paymentCompleteDesc: "Mock Order #{orderNumber} created. This is a prototype - no real payment was processed.",
      checkoutErrorDesc: "Failed to complete prototype checkout.",
      orderConfirmed: "Order Confirmed",
      orderConfirmedDesc: "Your order has been confirmed. Settings cannot be changed.",
      generationError: "Generation Error",
      generationErrorDesc: "Failed to generate character. Please try again.",
      error: "Error",
      noProjectError: "No project found. Please start over.",
      projectNotFoundError: "Project or user information not found.",
      pleaseWait: "Please wait…",
      checkingSignIn: "Checking your sign-in status.",
      noCharactersTryAgain: "No characters found. Try another image or name one manually.",
      analysisReady: "Analysis ready",
      analysisReadyDesc: "Pick and confirm a character, then Next to Magic to generate the image.",
      nameTooShort: "Please give your creation a name (at least 3 characters)",
      versionSelected: "Version selected",
      versionModelLoaded: "This version’s 3D model was loaded from storage.",
      versionSheetActive: "This reference sheet is now active. The 3D model must be regenerated.",
      versionSheetLoaded: "This version’s reference sheet was loaded from storage.",
      versionContinueSheet: "Version selected. Continue to generate a new reference sheet.",
      editApplied: "Edit applied.",
      reverted: "Reverted",
      revertedWithSheet: "Previous version and its reference sheet were restored.",
      revertedContinueSheet: "Previous version restored. Continue to generate a new sheet.",
      viewsEditApplied: "Views edit applied.",
      styleChangeConfirm: "Changing the style will discard the existing character, reference sheet and 3D model — you will need to regenerate them. Continue?",
      rerunMagic: "Re-run Magic Preview",
      styleChanged: "Style changed — click Analyse to regenerate the character.",
      renameConfirm: "Renaming will discard the existing reference sheet and 3D model — you will need to regenerate them. Continue?",
      welcomeBackProgress: "Welcome back!",
      welcomeBackProgressDesc: "Your progress has been saved. You're on step {current} of {total}.",
      couldNotCreateProject: "Could not create project. Please try again.",
      rerunAnalyse: "Please re-run Analyse before confirming a character.",
      analysisInProgress: "Please wait — analysis is still in progress.",
      analyseAndPick: "Please analyse the image and pick a character before continuing.",
      pickAndConfirmName: "Please pick a character and confirm a name before continuing.",
      runAnalyseFirst: "Please run Analyse on your image first.",
      toyNameMin: "Toy name must be at least 3 characters",
      toyNameMax: "Toy name must be less than 50 characters",
      toyNameChars: "Only letters, numbers, spaces, hyphens and underscores allowed",
    },
  },
  
  // File upload components
  fileUpload: {
    dropzone: {
      dropPhoto: "Drop their photo here",
      dropArtwork: "Drop their masterpiece here",
      orClick: "or click to browse",
      supportedFormats: "Supported formats: PNG, JPG, JPEG, WebP, AVIF, GIF, HEIC",
      maxSize: "Maximum file size: 20MB",
      invalidType: "Invalid file type",
      onlyAllowed: "Only PNG, JPG, JPEG, WebP, AVIF, GIF, and HEIC files are allowed",
      dropHere: "Drop your image here!",
      fileTooLarge: "File is too large ({size}MB). Maximum size is {max}MB.",
      couldNotRead: "Could not read this image. Please try another photo.",
      uploading: "Uploading...",
      choosePhotoGallery: "Choose Photo from Gallery",
      chooseDrawingGallery: "Choose Drawing from Gallery",
      takePhoto: "Take a Photo",
      tipsPhoto: "Tips for a great photo",
      tipsUpload: "Tips for a great upload",
      tipsTitle: "Upload Tips",
      tipPhoto1: "Good lighting — near a window works best",
      tipPhoto2: "Plain background",
      tipPhoto3: "One person in the frame",
      tipPhoto4: "Close-up shot",
      tipUpload1: "Place the drawing on a flat surface",
      tipUpload2: "Even lighting — avoid shadows",
      tipUpload3: "Capture the entire drawing",
      tipUpload4: "Shoot straight down from above",
    },
    preview: {
      uploadedImage: "Uploaded Image",
      previouslyUploaded: "Previously uploaded image",
      replace: "Replace",
      analyse: "Analyse",
      reanalyse: "Reanalyse",
      addCharacter: "Add Character",
      characterAdded: "Character added",
      characterConfirmed: "“{name}” is selected and will be turned into a toy.",
      characterConfirmedStory: "“{name}” was added to your story characters.",
      characterConfirmedAnimation: "“{name}” was added to your animation characters.",
      addCharacters: "Add {count} characters",
      charactersAdded: "Added",
      drawingLabel: "Drawing",
      generatedPhoto: "Generated photo",
      googleDrive: "Google Drive",
      local: "Local",
      drawing: "Drawing",
      photo: "Photo",
      typeQuestion: "This is a"
    }
  },

  auth: {
    brand: "Rubby",
    tagline: "Transform your sketches into real toys",
    signIn: { 
      title: "Welcome Back", 
      subtitle: "Sign in to continue creating amazing toys",
      button: "Sign In",
      buttonLoading: "Signing In...",
      noAccount: "Don't have an account?",
      createOne: "Create one now",
      tabPassword: "Password",
      tabOtp: "One-time code"
    },
    otp: {
      title: "Sign in with code",
      subtitle: "Get a verification code by email or SMS",
      channelEmail: "Email",
      channelSms: "SMS",
      phone: "Mobile number",
      phonePlaceholder: "09123456789",
      code: "Verification code",
      codePlaceholder: "6 digits",
      sendCode: "Send code",
      sendCodeLoading: "Sending...",
      verify: "Verify & sign in",
      verifyLoading: "Verifying...",
      resend: "Resend code",
      changeDestination: "Change email / phone",
      codeSent: "Code sent",
      codeSentDesc: "Enter the verification code.",
      codeSentDev: "Dev code",
      invalidPhone: "Enter a valid Iranian mobile (e.g. 09123456789)",
      invalidCode: "Code must be 6 digits",
      cooldown: "Please wait before requesting another code"
    },
    signUp: { 
      title: "Create Account", 
      subtitle: "Start your creative journey with Rubby",
      button: "Send verification code",
      buttonLoading: "Sending code...",
      hasAccount: "Already have an account?",
      signInInstead: "Sign in instead",
      checkSpam: "Didn't receive the email? Check your spam folder or try again.",
      backToSignIn: "Back to Sign In",
      verifyTitle: "Verify your email",
      verifySubtitle: "Enter the 6-digit code we sent to your email"
    },
    forgotPassword: { 
      title: "Reset Password", 
      subtitle: "Enter your email to receive a password reset link",
      button: "Send Reset Link",
      buttonLoading: "Sending Reset Link...",
      remember: "Remember your password?",
      backToSignIn: "Back to sign in"
    },
    resetPassword: {
      title: "Set New Password",
      subtitle: "Choose a strong password for your account",
      button: "Update Password",
      buttonLoading: "Updating Password..."
    },
    fields: {
      email: "Email",
      emailPlaceholder: "Enter your email",
      password: "Password",
      passwordPlaceholder: "Enter your password",
      confirmPassword: "Confirm Password",
      confirmPasswordPlaceholder: "Confirm your password"
    },
    passwordStrength: {
      label: "Password Strength:",
      veryWeak: "Very Weak",
      weak: "Weak",
      fair: "Fair",
      good: "Good",
      strong: "Strong",
      minRequired: "Password strength must be at least \"Fair\" to create an account."
    },
    validation: {
      emailRequired: "Please enter a valid email address",
      passwordMin: "Password must be at least 6 characters",
      passwordsNoMatch: "Passwords don't match",
      passwordTooWeak: "Password Too Weak",
      passwordTooWeakDesc: "Please choose a stronger password (minimum: Fair strength)."
    },
    errors: {
      generic: "An error occurred",
      invalidCredentials: "Invalid email or password",
      userExists: "An account with this email already exists",
      emailNotConfirmed: "Please check your email and click the confirmation link",
      accountBanned: "This account has been banned.",
      accountSuspended: "This account is temporarily suspended.",
      unexpected: "An unexpected error occurred. Please try again."
    },
    toasts: {
      resetEmailSent: "Reset Email Sent",
      resetEmailSentDesc: "Check your email for a password reset link.",
      resetEmailError: "Failed to send reset email.",
      passwordUpdated: "Password Updated",
      passwordUpdatedDesc: "Your password has been successfully reset.",
      passwordUpdateError: "Failed to update password.",
      accountCreated: "Code sent",
      accountCreatedDesc: "Enter the verification code from your email to create your account.",
      welcomeBack: "Welcome back!",
      welcomeBackDesc: "You have been signed in successfully.",
      authError: "Authentication Error",
      emailRequired: "Email Required",
      emailRequiredDesc: "Please enter your email address."
    },
    footer: {
      terms: "By continuing, you agree to our",
      termsLink: "Terms of Service",
      and: "and",
      privacyLink: "Privacy Policy"
    },
    separator: "or"
  },

  orderTracking: {
    title: "Track Your Order",
    subtitle: "Enter your tracking code to see the status of your custom toy",
    searchPlaceholder: "Enter tracking code (e.g., DT2024-ABC123)",
    searchButton: "Track Order",
    searching: "Searching...",
    orderNumber: "Order",
    tracking: "Tracking",
    customer: "Customer",
    deliveryAddress: "Delivery Address",
    estimatedDelivery: "Estimated Delivery",
    productionProgress: "Production Progress",
    started: "Started",
    complete: "Complete",
    delivered: "Delivered",
    inProgress: "In Progress",
    yourToy: "Your Custom Toy",
    modelPreview: "3D Model Preview",
    toyDescription: "Your custom toy based on the uploaded drawing",
    viewAR: "View in AR",
    statuses: {
      received: "Received",
      printing: "Printing",
      postProcessing: "Processing",
      painting: "Painting",
      qa: "Quality Check",
      packed: "Packed",
      shipped: "Shipped"
    }
  },

  toast: {
    success: { saved: "Changes saved successfully", uploaded: "File uploaded successfully", orderPlaced: "Order placed successfully", copied: "Copied to clipboard" },
    error: { generic: "Something went wrong. Please try again.", upload: "Failed to upload file", save: "Failed to save changes", network: "Network error. Please check your connection." },
    info: { processing: "Processing your request...", generating: "Generating your design..." },
  },

  editErrors: {
    contentRefused:
      "The AI could not work with this photo — this often happens with photos of adults or well-known people. Please try a clear photo of the child, or another picture.",
    unavailable:
      "The AI service did not respond. Your credit was not spent — please try again in a few moments.",
    generic: "The edit could not be completed. Please try again.",
  },

  storybook: {
    pageTitle: "Create Your Storybook",
    pageSubtitle: "Turn your child's drawing into a fully illustrated story",
    progress: "Progress",
    stepOf: "Step {current} of {total}",

    steps: {
      upload: "Show Umi",
      uploadDesc: "Upload a drawing or photo for Umi",
      magicPreview: "The Magic",
      magicPreviewDesc: "Watch Umi turn it into a character",
      concept: "The Story",
      conceptDesc: "Shape the story",
      style: "The Look",
      styleDesc: "Pick the art style",
      tale: "Umi's Tale",
      taleDesc: "Umi writes & illustrates your book",
      printOptions: "Make it Yours",
      printOptionsDesc: "Choose size, cover and paper",
      checkout: "Checkout",
      checkoutDesc: "Secure prototype checkout",
      confirmation: "Hooray!",
      confirmationDesc: "Your storybook is ready",
    },

    fulfillment: {
      title: "How would you like your storybook?",
      subtitle: "Pick a tier to continue",
      required: "Pick a tier to continue",
      catalogUnavailable: "Prices are unavailable. Retry loading the catalog.",
      retryCatalog: "Retry prices",
      pdfIncluded: "PDF included",
      options: {
        free: {
          title: "Free PDF",
          desc: "Download your 10-page illustrated PDF instantly.",
        },
        voice: {
          title: "Voice Narration",
          desc: "Page-by-page narration with a child-friendly voice.",
        },
        printed: {
          title: "Printed Book",
          desc: "Hardcover book shipped to your door.",
        },
        both: {
          title: "Printed + Voice",
          desc: "Everything: printed book, narration, and the PDF.",
        },
      },
    },

    printOptions: {
      title: "Make it Yours",
      subtitle: "Pick how your printed book should feel",
      included: "Included",
      totalLabel: "Total",
      size: {
        label: "Size",
        standard: "Standard · 20×20 cm",
        large: "Large · 28×28 cm",
      },
      cover: {
        label: "Cover",
        hardcover: "Hardcover",
        linen: "Premium linen",
      },
      paper: {
        label: "Paper",
        matte: "Matte",
        glossy: "Glossy",
      },
      giftBox: {
        label: "Gift box",
        desc: "Ship it in a keepsake gift box, ready to give.",
      },
    },

    checkout: {
      title: "Checkout",
      subtitle: "Review and complete your order",
      total: "Total",
      payButton: "Pay {price}",
      payButtonProcessing: "Processing...",
      prototypeMode: "Prototype Mode",
      prototypeDesc: "This is a prototype checkout. No real payment is processed.",
      prototypeNote: "No card is charged. This simulates a successful payment.",
      shippingTitle: "Shipping address",
      shippingRequired: "A shipping address is required for printed books.",
      quoteRequired: "Please lock a quote before paying.",
      catalogError: "Catalog price is unavailable. Please retry.",
      paymentFailed: "Payment failed. Please try again.",
      optional: "optional",
      fullName: "Full name",
      line1: "Address line 1",
      line2: "Address line 2",
      city: "City",
      province: "State / Province",
      postalCode: "Postal code",
      country: "Country",
      phone: "Phone",
    },

    confirmation: {
      title: "Hooray! Your storybook is ready",
      subtitle: {
        free: "Download your PDF below and enjoy your story.",
        voice: "Your storybook is ready. Narration is being prepared.",
        printed: "Your printed book is on the way!",
        both: "Your printed book is on the way and narration is being prepared.",
      },
      orderNumber: "Order #",
      trackOrder: "Track Order",
      downloadPDF: "Download PDF",
      voiceTitle: "Voice Narration",
      voicePreparing: "We're preparing your narration. You'll find it in your library shortly.",
      voiceReady: "Narration is ready to play in your library.",
    },

    input: {
      title: "Let's Create a Story!",
      subtitle: "Upload your child's drawing and tell us the story",
      uploadDrawing: "Upload your child's drawing",
      charactersTrayTitle: "Characters",
      removeCharacterTitle: "Remove this character?",
      removeCharacterDescription: "This character will be removed from the story.",
      removeCharacterConfirm: "Remove",
      removeCharacterCancel: "Cancel",
      characterRemoved: "Character removed",
      storyMethod: "How do you want to tell the story?",
      writeIt: "Write it",
      recordIt: "Record it",
      storyPlaceholder: "A little girl finds a magic car that can fly and she takes her cat on an adventure to the moon...",
      recording: "Recording...",
      recordingMax: "Max 1 minute",
      tapToRecord: "Tap to record",
      tapToStop: "Tap to stop",
      reRecord: "Re-record",
      useRecording: "Use this recording",
      childAge: "Child's age",
      ageToddler: "Toddler",
      ageEarlyReader: "Early Reader",
      ageChapterBook: "Chapter Book",
      illustrationStyle: "Illustration style",
      styleWatercolor: "Watercolor",
      styleCartoon: "Cartoon",
      styleCrayon: "Crayon",
      styleClassic: "Classic",
      styleAnime: "Anime",
      comingSoon: "Coming Soon",
      voiceNarration: "Story reader voice",
      animatedStory: "Animated story",
      micDenied: "Microphone access denied. Please allow microphone access or type your story instead.",
      drawingRequired: "Please upload a drawing to continue.",
      storyRequired: "Please write or record a story (at least 10 characters).",
      audioRequired: "Please record your story first.",
      illustrationStyleRequired: "Please select an illustration style to continue.",
      childAgeRequired: "Please select a child's age group to continue.",
      authorName: "Author Name",
      authorPlaceholder: "Who wrote this story?",
      selectChild: "Pick a child from your profile",
      customAuthor: "Or type a name",
      authorRequired: "Please enter an author name to continue.",
      storyLanguage: "Story language",
      languageEnglish: "English",
      languagePersian: "فارسی",
    },

    style: {
      title: "Style your storybook",
      subtitle: "Pick the audience and narrator.",
      voiceHint: "Add a voice narrator to read the story aloud",
    },

    concept: {
      title: "Tell us your story",
      subtitle: "Write it down or record yourself telling it.",
      transcribing: "Transcribing your recording…",
    },

    transcript: {
      title: "Review Your Story Idea",
      rawLabel: "We heard this from the recording:",
      warning: "We couldn't catch some words. Please fix anything that looks wrong.",
      editLabel: "Edit the story below:",
      replayAudio: "Replay audio",
      confirm: "Confirm Story",
    },

    image: {
      title: "Magic Preview",
      subtitle: "Here's your drawing come to life!",
      originalLabel: "Original Drawing",
      resultLabel: "Magic Result",
      addNotes: "Add notes (optional)",
      notesPlaceholder: "Make the dog bigger, she should be smiling...",
      regenerate: "Regenerate",
      edit: "Edit",
      submitEdit: "Submit Edit",
      generating: "Transforming your drawing...",
    },

    generate: {
      title: "Creating Your Storybook",
      analyzing: "Analyzing your drawing...",
      writing: "Writing your story...",
      characters: "Creating character designs...",
      illustrating: "Illustrating pages...",
      pageProgress: "Page {current} of {total}",
      assembling: "Assembling your storybook...",
      estimatedTime: "This usually takes about 2 minutes",
      ready: "Your Storybook is Ready!",
      summary: "{pages} pages · {readTime} min read",
      reviewButton: "Review Your Book",
      assetsProgress: "{current}/{total} assets",
      previewSoFar: "Saved so far",
      voicePagesProgress: "{current}/{total} pages",
      regenConfirm:
        "A new story will be generated and later steps will run again (credits may be charged). Continue?",
      error: {
        title: "Something went wrong",
        savedMessage: "Don't worry — your story and settings are saved. Tap Try Again to resume from where it failed.",
        retryButton: "Try Again",
        storyFailed: "Failed while writing the story. Your drawing and settings are saved.",
        promptsFailed: "Failed while planning illustrations. Your story was saved.",
        assetsFailed: "Failed while creating character references.",
        illustrationsFailed: "Failed while drawing the pages.",
        fallback: "Generation failed. Please try again.",
      },
      voice: {
        title: "Voice Narration",
        enriching: "Enriching story text...",
        recording: "Recording narration...",
        complete: "Narration complete",
      },
      theater: {
        stages: [
          "Studying every detail...",
          "Mapping the dimensions...",
          "Mixing the digital paint...",
          "Assembling your creation...",
          "Polishing the final sparkle...",
        ],
        messages: [
          "Measuring dragon wings...",
          "Mixing the digital paint...",
          "Heating up the kiln...",
          "Scanning for maximum cuteness...",
          "Adding the secret sparkle...",
          "Polishing the final details...",
          "Almost there — perfection takes a moment...",
        ],
        timeEstimate: "This usually takes 60–90 seconds",
        funFacts: [
          "Did you know? Every illustration is generated uniquely — no two storybooks are ever the same.",
          "Did you know? Our AI preserves every quirk from the original drawing, even wobbly lines.",
          "Did you know? Each storybook page is illustrated with your child's art style in mind.",
          "Did you know? We use the same AI tech that powers professional animation studios.",
          "Did you know? Your child's drawing is the creative director — the AI just follows orders.",
        ],
      },
    },

    review: {
      title: "Review Your Storybook",
      pageOf: "Page {current} of {total}",
      editText: "Edit Story Text",
      editIllustration: "Edit Illustration",
      submitIllustrationEdit: "Submit Edit",
      editTitle: "Edit Title",
      changeAuthor: "Change Author",
      regenerateCover: "Regenerate Cover",
      previous: "Previous",
      next: "Next",
      lookGreat: "Looks Great! Continue",
      theEnd: "The End",
      madeWith: "Made with love by Rubby",
      coverFallback: "Your Story",
      flipHint: "Click or swipe to turn pages",
      prevPage: "Previous page",
      nextPage: "Next page",
      backCoverTagline: "Every drawing tells a story",
      readAloud: "Auto-Play Story",
      stopReading: "Stop Auto-Play",
      playNarration: "Play narration",
      pauseNarration: "Pause narration",
      pageLabel: "Page {pageNumber}",
      noPagesToDisplay: "No pages to display",
    },

    extras: {
      title: "Animation",
      voiceTitle: "Story Reader Voice",
      voiceDesc: "Each page will be read aloud with a child-friendly narrator voice.",
      animationTitle: "Animated Story",
      animationDesc: "Watch your storybook come alive as an animated video.",
      notifyMe: "Notify me when available",
      emailPlaceholder: "your@email.com",
    },

    download: {
      title: "Get Your Storybook",
      freeTitle: "Digital Storybook",
      freePrice: "Free",
      freeFeature1: "10-page illustrated PDF",
      freeFeature2: "Shareable link",
      freeButton: "Download PDF",
      premiumTitle: "With Voice Narration",
      premiumPrice: "$9.99",
      premiumFeature1: "Everything in Free",
      premiumFeature2: "Page-by-page narration",
      premiumFeature3: "Choice of voices",
      premiumPlusTitle: "With Animation",
      premiumPlusPrice: "$19.99",
      premiumPlusFeature1: "Everything in Premium",
      premiumPlusFeature2: "Animated story video",
      physicalTitle: "Printed Hardcover",
      physicalPrice: "$29.99",
      physicalFeature1: "Premium hardcover book",
      physicalFeature2: "High-quality paper",
      physicalFeature3: "Shipped to your door",
    },

    newProject: {
      button: "New Project",
      title: "Start a new storybook?",
      description: "Your current progress will be lost.",
      cancel: "Cancel",
      confirm: "Start New",
    },
  },

  notFound: {
    subtitle: "Oops! Page not found",
    returnHome: "Return to Home",
  },

  admin: {
    verifyingAccess: "Verifying Access",
    checkingPrivileges: "Checking your administrator privileges...",
    accessDenied: "Access Denied",
    noAdminAccess: "You do not have administrator privileges to access this page.",
    returnHome: "Return to Home",
    title: "Admin Dashboard",
    subtitle: "Manage orders, projects, and monitor production status",
    totalOrders: "Total Orders",
    totalRevenue: "Total Revenue",
    avgOrderValue: "Avg Order Value",
    shippedOrders: "Shipped Orders",
    totalProjects: "Total Projects",
    ordersTab: "Orders",
    projectsTab: "Projects",
    searchOrders: "Search orders...",
    searchProjects: "Search by title, email, order...",
    allStatus: "All Status",
    allStyles: "All Styles",
    allTypes: "All Types",
    loadingOrders: "Loading orders...",
    loadingProjects: "Loading projects...",
    noOrdersFound: "No orders found",
    noProjectsFound: "No projects found",
    updating: "Updating...",
    statusUpdated: "Status Updated",
    statusUpdatedDesc: "Order status changed to {status}",
    updateFailed: "Update Failed",
    updateFailedDesc: "Failed to update order status",
    errorLoadingOrders: "Error Loading Orders",
    errorLoadingOrdersDesc: "Failed to load orders from database",
    errorLoadingProjects: "Error Loading Projects",
    errorLoadingProjectsDesc: "Failed to load projects from database",
    orderNumber: "Order #",
    customer: "Customer",
    email: "Email",
    project: "Project",
    status: "Status",
    total: "Total",
    created: "Created",
    actions: "Actions",
    preview: "Preview",
    projectTitle: "Title",
    userEmail: "User Email",
    type: "Type",
    style: "Style",
  },

  orderDetails: {
    loadingOrder: "Loading Order",
    fetchingDetails: "Fetching order details...",
    accessDenied: "Access Denied",
    noPermission: "You do not have permission to view order details.",
    backToDashboard: "Back to Dashboard",
    orderNotFound: "Order Not Found",
    orderNotFoundDesc: "The order you're looking for doesn't exist or has been removed.",
    order: "Order",
    createdAt: "Created {date}",
    customerInfo: "Customer Information",
    name: "Name",
    email: "Email",
    phone: "Phone",
    shippingAddress: "Shipping Address",
    projectDetails: "Project Details",
    projectTitle: "Project Title",
    styleLabel: "Style",
    sizeLabel: "Size",
    materialLabel: "Material",
    colorPlan: "Color Plan",
    statusHistory: "Status History",
    noStatusHistory: "No status history available",
    orderSummary: "Order Summary",
    orderTotal: "Order Total",
    paymentStatus: "Payment Status",
    paid: "Paid",
    pending: "Pending",
    orderNumber: "Order Number",
    lastUpdated: "Last Updated",
    trackingInfo: "Tracking Information",
    trackingCode: "Tracking Code",
    trackingPlaceholder: "Enter tracking code",
    estimatedDelivery: "Estimated Delivery",
    updateTracking: "Update Tracking",
    updating: "Updating...",
    trackingUpdated: "Tracking Updated",
    trackingUpdatedDesc: "Tracking information has been updated successfully",
    trackingUpdateFailed: "Update Failed",
    trackingUpdateFailedDesc: "Failed to update tracking information",
    errorLoadingOrder: "Error Loading Order",
    errorLoadingOrderDesc: "Failed to load order details",
  },

  qualitySafety: {
    hero: {
      title: "Quality & Safety at Rubby",
      subtitle: "Your child's safety and happiness are non-negotiable. Here's how we protect both.",
    },
    materials: {
      title: "Our Materials",
      subtitle: "Every material is independently tested and certified for children's use",
      pla: {
        name: "Standard PLA",
        description: "Biodegradable, plant-based, smooth finish. Best for display and light play.",
        badges: ["BPA-Free", "FDA-Compliant", "Biodegradable"],
      },
      petg: {
        name: "Tough PETG",
        description: "Impact-resistant, vibrant colors. Built for active play.",
        badges: ["Child-Safe Certified", "Impact-Resistant", "Vibrant Color"],
      },
      tpu: {
        name: "Flexible TPU",
        description: "Soft, bendy, huggable. Perfect for young children.",
        badges: ["Non-Toxic", "Flexible", "Soft Touch"],
      },
      resin: {
        name: "High-Detail Resin",
        description: "Museum-smooth finish, photorealistic detail. Best for display pieces and collectibles.",
        badges: ["Ultra-Smooth", "Photorealistic", "Collector-Grade"],
      },
    },
    process: {
      title: "Our Quality Process",
      subtitle: "Every toy passes through three rigorous checkpoints before it reaches your child",
      steps: {
        inspection: {
          title: "Post-Print Inspection",
          description: "Checking structural integrity, layer consistency, and print quality against our standards.",
        },
        review: {
          title: "Post-Finish Review",
          description: "Verifying color accuracy, surface smoothness, and detail fidelity to the approved preview.",
        },
        approval: {
          title: "Pre-Ship Approval",
          description: "Final packaging check, damage prevention, and quality seal before dispatch.",
        },
      },
    },
    privacy: {
      title: "Your Child's Privacy",
      commitments: [
        "We never use your uploads to train AI models without explicit opt-in.",
        "Families retain full ownership of all artwork submitted.",
        "COPPA and GDPR-K aligned — parent/guardian account ownership required.",
        "Data deletion available on request — your art, your control.",
        "All uploads encrypted in transit and at rest.",
      ],
    },
    guarantee: {
      title: "100% Happiness Guarantee",
      description: "If the finished product doesn't match the approved preview, we'll remake it free or give a full refund. No hoops. No hassle.",
      button: "Start Creating — Risk Free",
    },
  },

  heroPanel: {
    riko: {
      chipLabel: "The toy maker",
      name: "Riko",
      hookBefore: "That wobbly monster on the fridge?",
      hookAfter: "Riko makes it ",
      hookHighlight: "real.",
      body: "Send a drawing or a favourite photo — Riko tickles it into a huggable toy that arrives at your door.",
      ctaLabel: "Make a friend with Riko",
      howLabel: "How the magic works",
    },
    umi: {
      chipLabel: "The storyteller",
      name: "Umi",
      hookBefore: "Every picture is whispering a story.",
      hookAfter: "Umi can ",
      hookHighlight: "hear it.",
      body: "She'll spin it into a picture book with your child as the hero — their name shining on the cover.",
      ctaLabel: "Open the book with Umi",
      howLabel: "How the magic works",
    },
    bibi: {
      chipLabel: "The animator",
      name: "Bibi",
      hookBefore: "Pictures get bored on the wall.",
      hookAfter: "Bibi ",
      hookHighlight: "wakes them up.",
      body: "Send a doodle or a snapshot and watch it blink, giggle, and dance in its own mini movie.",
      ctaLabel: "Wake it up with Bibi",
      howLabel: "How the magic works",
    },
    yoyo: {
      chipLabel: "The game maker",
      name: "Yoyo",
      hookBefore: "Yoyo never says \"don't touch\" —",
      hookAfter: "Yoyo says ",
      hookHighlight: "\"press start!\"",
      body: "Any drawing or photo becomes the star of its own little game — your child holds the controls.",
      ctaLabel: "Press start with Yoyo",
      howLabel: "How the magic works",
    },
  },
  animation: {
    length: {
      title: "Animation length",
      subtitle: "How long should the finished animation be?",
      secondsSuffix: "s",
    },

    steps: {
      upload: "Show Bibi",
      uploadDesc: "Upload a drawing or photo for Bibi",
      magicPreview: "The Magic",
      magicPreviewDesc: "Watch Bibi turn it into a character",
      concept: "The Story",
      conceptDesc: "Shape the story",
      style: "The Look",
      styleDesc: "Pick the art style",
      storyboardReview: "Scenes",
      storyboardReviewDesc: "Review keyframes before video",
      tale: "Bibi's Tale",
      taleDesc: "Bibi writes and animates your film",
      checkout: "Checkout",
      checkoutDesc: "Secure prototype checkout",
      confirmation: "Hooray!",
      confirmationDesc: "Your animation is ready",
    },

    storyboardReview: {
      title: "Review your scenes",
      subtitle:
        "Edit scene text or regenerate stills, then approve to render the film.",
      lockedSubtitle: "Scenes approved — stills are read-only while the film is rendering.",
      lockedHint: "Continue on The Tale step to watch progress or your finished film.",
      goToTale: "Go to The Tale",
      approve: "Approve scenes & generate animation",
      approving: "Starting video…",
      remakeVideo: "Regenerate video",
      remakeSubtitle:
        "You can edit scenes and start the film again from these keyframes.",
      remakeHint: "After editing text or stills, tap regenerate video.",
      editStoryboard: "Edit scenes & regenerate",
      editScene: "Edit scene",
      regenerate: "Regenerate still",
      missingImage: "Keyframe not ready",
      startFrame: "Start",
      endFrame: "End",
      waitingForImages: "Approve unlocks when every scene has start and end frames.",
      textSaved: "Scene text saved",
      textSaveFailed: "Could not save scene text",
      regenStarted: "Regeneration started",
      regenStartedDesc: "Refreshing the scene when ready…",
      regenFailed: "Regeneration failed",
      regenTimedOut: "Regeneration timed out — try again",
      editBusy: "Scene edit in progress…",
    },

    fulfillment: {
      title: "How would you like your animation?",
      subtitle: "Pick a tier to continue",
      required: "Pick a tier to continue",
      catalogUnavailable: "Prices are unavailable. Retry loading the catalog.",
      retryCatalog: "Retry prices",
      options: {
        free: {
          title: "Free Video",
          desc: "Your finished animation, instant MP4 download.",
        },
        hd: {
          title: "HD / 4K",
          desc: "Crisp HD/4K master, no watermark.",
        },
        premium: {
          title: "Premium",
          desc: "HD/4K plus character voice-over and background music.",
        },
        ultimate: {
          title: "Ultimate",
          desc: "Premium plus an extended, longer animation.",
        },
      },
    },

    checkout: {
      title: "Checkout",
      subtitle: "Review and complete your order",
      total: "Total",
      payButton: "Pay {price}",
      payButtonProcessing: "Processing...",
      prototypeMode: "Prototype Mode",
      prototypeDesc: "This is a prototype checkout. No real payment is processed.",
      prototypeNote: "No card is charged. This simulates a successful payment.",
      quoteRequired: "Please lock a quote before paying.",
      catalogError: "Catalog price is unavailable. Please retry.",
      paymentFailed: "Payment failed. Please try again.",
    },

    confirmation: {
      title: "Hooray! Your animation is ready",
      subtitle: {
        free: "Download your video below and enjoy.",
        hd: "Your HD/4K video is ready to download. Order details are in your account.",
        premium: "Your Premium animation is ready to download. Order details are in your account.",
        ultimate: "Your Ultimate animation is ready to download. Order details are in your account.",
      },
      orderNumber: "Order #",
      trackOrder: "Track Order",
      downloadVideo: "Download Video",
      videoStillRendering: "Your video is still rendering — try again in a moment.",
    },

    concept: {
      title: "Tell us your story",
      subtitle: "Write it down or record yourself telling it.",
      transcribing: "Transcribing your recording…",
    },

    style: {
      title: "Style your animation",
      subtitle: "Pick the audience and narrator.",
    },

    input: {
      writeIt: "Write it",
      recordIt: "Record it",
      storyPlaceholder: "A little girl finds a magic car that can fly and she takes her cat on an adventure to the moon...",
      recordingMax: "Max 1 minute",
      tapToRecord: "Tap to record",
      tapToStop: "Tap to stop",
      reRecord: "Re-record",
      useRecording: "Use this recording",
      childAge: "Child's age",
      ageToddler: "Toddler",
      ageEarlyReader: "Early Reader",
      ageChapterBook: "Chapter Book",
      illustrationStyle: "Illustration style",
      comingSoon: "Coming Soon",
      micDenied: "Microphone access denied. Please allow microphone access or type your story instead.",
      storyRequired: "Please write or record a story (at least 10 characters).",
      illustrationStyleRequired: "Please select an illustration style to continue.",
      childAgeRequired: "Please select a child's age group to continue.",
      authorName: "Author Name",
      authorPlaceholder: "Who wrote this story?",
      authorRequired: "Please enter an author name to continue.",
      storyLanguage: "Story language",
      languageEnglish: "English",
      languagePersian: "فارسی",
      uploadDrawing: "Upload drawings or photos",
    },

    transcript: {
      title: "Review Your Story Idea",
      rawLabel: "We heard this from the recording:",
      warning: "We couldn't catch some words. Please fix anything that looks wrong.",
      editLabel: "Edit the story below:",
      replayAudio: "Replay audio",
      confirm: "Confirm Story",
    },

    image: {
      notesPlaceholder: "Make the dog bigger, she should be smiling...",
    },

    generate: {
      ready: "Your Animation is Ready!",
      summary: "{pages} scenes · {readTime} min film",
      reviewButton: "Review Your Animation",
      assetsProgress: "{current}/{total} assets",
      previewSoFar: "Saved so far",
      pageProgress: "Scene {current} of {total}",
      regenConfirm:
        "A new story will be generated and later steps will run again (credits may be charged). Continue?",
      error: {
        title: "Something went wrong",
        savedMessage: "Don't worry — your story and settings are saved. Tap Try Again to resume from where it failed.",
        retryButton: "Try Again",
        storyFailed: "Failed while writing the story. Your drawing and settings are saved.",
        promptsFailed: "Failed while planning scenes. Your story was saved.",
        assetsFailed: "Failed while creating character references.",
        illustrationsFailed: "Failed while rendering the scenes.",
        storyboardsFailed: "We had trouble designing storyboards. Please retry.",
        scenePromptsFailed: "We had trouble composing scene prompts. Please retry.",
        videoFailed: "We had trouble rendering the video. Please retry.",
        fallback: "Generation failed. Please try again.",
      },
      phases: {
        writing: "Writing your story",
        mapping: "Mapping every detail",
        painting: "Painting reference art",
        drawing: "Drawing the still frames",
        storyboards: "Designing each shot (storyboards)",
        directing: "Directing the action (scene prompts)",
        filming: "Rolling the film (video)",
        polish: "Final polish",
      },
      theater: {
        stages: [
          "Studying every detail...",
          "Mapping the dimensions...",
          "Mixing the digital paint...",
          "Assembling your creation...",
          "Polishing the final sparkle...",
        ],
        messages: [
          "Measuring dragon wings...",
          "Mixing the digital paint...",
          "Heating up the kiln...",
          "Scanning for maximum cuteness...",
          "Adding the secret sparkle...",
          "Polishing the final details...",
          "Almost there — perfection takes a moment...",
        ],
        timeEstimate: "This usually takes 60–90 seconds",
        funFacts: [
          "Did you know? Every scene is generated uniquely — no two animations are ever the same.",
          "Did you know? Our AI preserves every quirk from the original drawing, even wobbly lines.",
          "Did you know? Each scene is directed with your child's art style in mind.",
          "Did you know? We use the same AI tech that powers professional animation studios.",
          "Did you know? Your child's drawing is the creative director — the AI just follows orders.",
        ],
      },
    },

    review: {
      title: "Review your animation",
      coverFallback: "Your Animation",
      noPagesToDisplay: "No scenes to display",
      watchHint: "Play your finished film — one short movie",
      videoNotReady: "The final video isn't ready yet. Scene clips are below.",
      scenesHint: "Scene clips",
      sceneLabel: "Scene {n}",
      flipHint: "Click or swipe to turn pages",
      prevPage: "Previous page",
      nextPage: "Next page",
      theEnd: "The End",
      madeWith: "Made with love by Rubby",
      backCoverTagline: "Every drawing tells a story",
      editText: "Edit Scene Text",
      editIllustration: "Edit Scene",
      submitIllustrationEdit: "Submit Edit",
    },


    newProject: {
      button: "New Project",
      title: "Start a new animation?",
      description: "Your current progress will be lost.",
      cancel: "Cancel",
      confirm: "Start New",
    },
  },

  productCta: {
    chooseHeading: "Pick your magic",
    chooseSubheading: "Three ways to turn your child's drawing or photo into something they'll treasure.",
    toy: "Make a Toy",
    storybook: "Make a Storybook",
    animation: "Make an Animation",
  },

  contactDetails: {
    generalEmail: "hello@rubby.co.uk",
    privacyEmail: "privacy@rubby.co.uk",
    legalEmail: "legal@rubby.co.uk",
    companyLine: "RUBBY — a creative studio for families, based in the United Kingdom.",
  },

  legalDates: {
    termsLastUpdated: "16 June 2026",
    privacyLastUpdated: "16 June 2026",
  },

  faqExtra: {
    digital: {
      title: "Storybooks, Animation & Credits",
      questions: {
        storybook: { q: "How does a personalised storybook work?", a: "You upload a drawing or photo of your child, pick a style, tell us a little about them, and our AI writes and illustrates a unique picture book with them as the hero. You can review every page, request changes, and add narration in English or Persian before you order a printed copy or download the PDF." },
        animation: { q: "What is an animated film made of?", a: "We turn one drawing or photo into a directed short film: a story, scene-by-scene visuals, character voices, and music. You see the storyboard first and approve each scene before final render. Lengths run from about 15 to 60 seconds today." },
        credits: { q: "Toys are priced per order. What about digital products?", a: "Storybooks and animated films use credit packs (and an optional subscription) so you can create more for less. One full storybook or one short animation uses one credit. Credits never expire while your subscription is active." },
        referral: { q: "Do you have a referral programme?", a: "Yes. Every account has a referral link in the Account page. When a friend places their first order you both receive credit, and you climb tiers as your friends create more." },
        birthday: { q: "What is the Birthday Club?", a: "Add your child's birthday in the Account page and we'll send a personalised discount every year on their birthday, redeemable across toys, storybooks, and animations." },
        delivery: { q: "Do digital products ship to me?", a: "Storybooks and animations are delivered instantly inside your account: stream, download, or order a printed book. Only physical toys ship in the post." },
      },
    },
  },

  qualitySafetyDigital: {
    title: "Digital Products — Safety & Trust",
    subtitle: "Storybooks and animations get the same care as our physical toys.",
    items: {
      contentReview: { title: "Age-appropriate content review", description: "Every storybook script and animation prompt passes through automated safety checks tuned for children's content." },
      parentApproval: { title: "Parents stay in control", description: "You review and approve characters, story, illustrations, and scenes at every step. Nothing is finalised without your sign-off." },
      noTraining: { title: "Your child's data is not training data", description: "Uploaded drawings, photos, voice recordings, and story prompts are used to create your order — never to train external AI models." },
      moderation: { title: "Bilingual moderation", description: "Our review pipeline works in both English and Persian, so safety standards travel with the language." },
    },
  },

  reviewsExtra: {
    soft: {
      headline: "Loved by families across the UK and beyond",
      subline: "A few of the stories families have shared after making something with RUBBY.",
    },
    items: {
      laila: { name: "Laila R.", location: "Manchester, UK", title: "Her name on the cover. Tears on mine.", review: "We made a bedtime storybook starring my daughter and her cat. She asks for it every night and 'reads' it back to us in two languages. Worth every penny.", date: "2 weeks ago", productLabel: "Storybook" },
      omar: { name: "Omar B.", location: "London, UK", title: "First animation, instant fan club", review: "Turned my son's spaceship drawing into a 30-second mini-film. The whole family watched it on repeat. He keeps asking which scene we can change next.", date: "1 month ago", productLabel: "Animation" },
      nia: { name: "Nia P.", location: "Bristol, UK", title: "Storybook in Persian and English", review: "Our grandparents speak Farsi and our daughter is growing up in the UK. Having the same story read in both languages is something we didn't know we needed.", date: "3 weeks ago", productLabel: "Storybook" },
      karim: { name: "Karim S.", location: "Edinburgh, UK", title: "Director's cut at age 6", review: "The storyboard step is brilliant — my son sat with me and approved each scene like a tiny director. Final film looks better than I imagined.", date: "5 days ago", productLabel: "Animation" },
    },
  },

  termsExtra: {
    digital: {
      title: "14. Digital Products & Credits",
      content: "Storybooks and animated films are digital deliverables created on demand. Credits are non-transferable. A credit is consumed when you confirm final generation; once consumed it is not refundable. Where a final delivery fails our quality checks we will re-run the job at no additional credit cost.",
    },
    subscriptions: {
      title: "15. Subscriptions",
      content: "Each billing period starts when Zarinpal confirms payment. Credits for that period are granted immediately. The next period starts only after you pay again — we cannot charge your card automatically. You may cancel any time; cancellation takes effect at the end of the current paid period. Unused recurring credits do not roll over after cancellation.",
    },
    referral: {
      title: "16. Referral Programme & Birthday Club",
      content: "Referral credit is awarded when a referred customer completes their first paid order. Birthday discounts are issued once per child per calendar year. We may suspend or revoke rewards in cases of abuse, including self-referrals, duplicate accounts, or fraudulent activity.",
    },
    acceptableUse: {
      title: "17. Acceptable Use",
      intro: "When you use RUBBY you agree not to upload or request content that is:",
      items: [
        "Sexual, violent, hateful, or otherwise harmful to children",
        "Infringing someone else's copyright, trademark, or likeness rights",
        "Designed to impersonate a real person without their consent",
        "Intended to deceive, harass, or harm others",
      ],
      note: "Parents and guardians are responsible for content uploaded under their account. We may refuse or remove any order that breaches these rules.",
    },
    governingLaw: {
      title: "18. Governing Law",
      content: "These terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction over any dispute, save that consumers may also bring claims in the courts of their country of residence where required by local law.",
    },
  },

  privacyExtra: {
    userContent: {
      title: "4a. Your Drawings, Photos, Voice & Stories",
      intro: "We treat everything you create or upload through RUBBY as your content:",
      items: [
        "Drawings and photos uploaded for toys, storybooks, or animations",
        "Story prompts, character names, and personal details you provide",
        "Voice recordings used for narration or character voices",
        "Generated illustrations, 3D models, audio, and video produced for your order",
      ],
      note: "We use this content to create your order and to let you review and re-generate it. We do not sell it and we do not use it to train external AI models. Gallery sharing is always opt-in.",
    },
    billing: {
      title: "11a. Subscription & Payment Data",
      content: "Subscriptions and credit packs are billed through Zarinpal. We store your subscription status, plan, and billing dates. We never store full card numbers.",
    },
    referralBirthday: {
      title: "11b. Referral & Birthday Club Data",
      content: "If you join the referral programme we store your referral code, the accounts that signed up through it, and the rewards earned. If you opt your child into the Birthday Club we store their first name and birth month/year to send personalised discounts. You can opt out and delete this data at any time from your Account page.",
    },
    aiProcessing: {
      title: "11c. AI Processing",
      content: "To generate illustrations, 3D models, scripts, voices, and animations we send the content you provide to specialist AI providers under contracts that prohibit training on your data. We log the request, response, and timing for debugging and safety, and we delete those logs on a rolling 90-day basis.",
    },
  },

  contactExtra: {
    quickLinks: {
      story: "Create a personalised storybook",
      animation: "Create an animated short film",
    },
  },

  howItWorksExtra: {
    tabs: {
      toy: "Toys",
      storybook: "Storybooks",
      animation: "Animation",
    },
    storybook: {
      title: "How a personalised storybook is made",
      steps: {
        upload: { title: "Upload & tell us about your child", description: "Add a drawing or photo, your child's name, age, and a few details you'd like the story to celebrate." },
        story: { title: "AI writes & illustrates", description: "We generate a script, characters, and page-by-page illustrations in your chosen style. You see every page." },
        narration: { title: "Optional narration", description: "Add an English or Persian voice (or record your own) so the book reads itself at bedtime." },
        deliver: { title: "Read online or order in print", description: "Stream the book in our flip-book reader, download the PDF, or order a printed copy." },
      },
    },
    animation: {
      title: "How an animated short film is made",
      steps: {
        upload: { title: "Upload a drawing or photo", description: "Just one image is enough — a character, a place, or a snapshot of your child." },
        story: { title: "Approve the story & storyboard", description: "We propose a story and a scene-by-scene storyboard. Edit, swap, or re-run anything you want before we render." },
        direct: { title: "Direct the scenes", description: "Behind the scenes we generate per-scene directing notes (camera, lighting, mood) so the final film feels intentional, not random." },
        watch: { title: "Watch & share", description: "Final film lands in your account in minutes. Download MP4, share a link, or order it bundled with a matching toy." },
      },
    },
  },

  heroNav: {
    toy: 'Toy',
    story: 'Story',
    animation: 'Animation',
    game: 'Game',
    gallery: 'Gallery',
    howItWorks: 'How it works',
    pricing: 'Pricing',
    reviews: 'Reviews',
    products: 'Rubby products',
  },

  magicPreview: {
    title: "Magic preview",
    subtitle: "Here is a styled character for each person — used to keep them consistent across pages.",
    subtitleAnimation: "Here is a styled character for each person — used to keep them consistent across scenes.",
    empty: "No characters yet. Go back to Upload to add one.",
    analyseSource: "Analyse source",
    originalAlt: "Original",
    referenceCharacter: "Reference character",
    generating: "Generating character…",
    notGenerated: "Not generated yet",
    editLabel: "Describe the changes you want",
    editPlaceholder: "e.g. make the hair curlier, add a red scarf",
    editNeedDetail: "Please add more detail",
    editNeedDetailDesc: "Describe the changes (at least 10 characters).",
    submitEdit: "Submit Edit",
    generate: "Generate",
    regenerate: "Regenerate",
    edit: "Edit",
    nameRequired: "Name required",
    nameRequiredDesc: "Please set a name for this character first.",
    generateUnavailable: "Generate unavailable",
    generateUnavailableDesc: "Reload the page and try again.",
  },

  viewer3d: {
    loaded: "3D model of {name} loaded",
    loadedFallback: "your toy",
    loadError: "Error loading 3D model",
    sizeChanged: "Size changed to {value}",
    materialChanged: "Material changed to {value}",
    colorChanged: "Color changed to {value}",
    cameraReset: "Camera reset",
    interactive: "Interactive 3D preview",
    ofName: "of {name}",
    size: "Size: {value}",
    material: "Material: {value}",
    instructions: "Use arrow keys or mouse to rotate the model. Use mouse wheel or pinch to zoom in and out.",
    cameraAngles: "Camera angles",
    resetCamera: "Reset camera",
    front: "Front",
    back: "Back",
    left: "Left",
    right: "Right",
    top: "Top",
    loadingViewer: "Loading 3D viewer...",
    fullColor: "Full Color",
    sizeComparison: "Size Comparison",
    hideSizeRef: "Hide size reference",
    showSizeRef: "Show size reference",
    loadingModel: "Loading 3D model...",
    sizeSmall: "8 cm / 3\"",
    sizeMedium: "12 cm / 5\"",
    sizeLarge: "18 cm / 7\"",
    sizeVeryLarge: "24 cm / 10\"",
    materialPla: "Standard PLA",
    materialPetg: "Tough PETG",
    materialTpu: "Flexible TPU",
    materialResin: "High Detail Resin",
  },

  shareCard: {
    shareFailed: "Could not create a share link",
    shareText: "Look what I created with RUBBY! 🎨🧸 {name}",
    imageDownloaded: "Image downloaded!",
    downloadFailed: "Download failed",
    checkOut: "Check out what I created with RUBBY!",
    linkNotReady: "Share link is not ready yet",
    linkCopied: "Link copied!",
    copyFailed: "Failed to copy",
    title: "Share Your Creation",
    originalDrawing: "Original drawing",
    toy3d: "3D toy",
    madeWith: "Made with RUBBY — From drawing to real toy",
    saving: "Saving...",
    downloadImage: "Download Image",
    copied: "Copied!",
    copyLink: "Copy Link",
    twitter: "Share on Twitter",
    facebook: "Share on Facebook",
    whatsapp: "Share on WhatsApp",
    email: "Share via Email",
  },

  resumeProject: {
    title: "Resume Your Project?",
    unfinished: "You have an unfinished project from {timeAgo}.",
    discount: "15% off to complete your order!",
    code: "Code:",
    continue: "Continue Where I Left Off",
    startFresh: "Start Fresh",
    minutesAgo: "{n} minutes ago",
    hoursAgo: "{n} hours ago",
    daysAgo: "{n} days ago",
    previewAlt: "Project preview",
  },

  priceBreakdown: {
    digitalModel: "Digital 3D model (.glb)",
    baseToy: "Base toy",
    sizeUpgrade: "Size upgrade",
    materialUpgrade: "Material upgrade",
    fullColor: "Full color",
    addModel: "Add 3D model (.glb)",
    title: "Price Breakdown",
    subtotal: "Subtotal",
    shipping: "Shipping",
    total: "Total",
    instantDownload: "Instant download after payment",
    moneyBack: "30-day money-back guarantee",
    toggle: "Toggle price breakdown",
    estimatedTotal: "Estimated Total",
    basePrice: "Base Price",
    sizeUpgradeShort: "Size Upgrade",
    materialUpgradeShort: "Material Upgrade",
    colorUpgrade: "Color Upgrade",
  },

  paymentConfirm: {
    title: "Confirm Your Order",
    total: "Total:",
    guaranteeTitle: "Our 100% Happiness Guarantee",
    guaranteeBody: "If you're not happy with your toy, we'll refund you within 30 days. No questions asked.",
    paySecurely: "Pay Securely",
  },

  gamePage: {
    title: "Game",
    seoTitle: "Game | Rubby",
    seoDescription: "Play games featuring your child's drawings. Coming soon.",
    badge: "Coming Soon",
    body: "We're crafting something playful. Stay tuned!",
  },

  photoToGame: {
    title: "Photo to Game",
    subtitle: "Coming soon — turn your favorite photo into a playable mini-game.",
    body: "We're crafting something playful. Check back shortly, or explore our other creators in the meantime.",
    backHome: "Back to home",
  },

  kitchen: {
    headline: "Rubby is the best to convert your imagination to real product using new technology and real time processing",
  },

  sharedCreation: {
    incomplete: "This share link is incomplete",
    notFound: "We couldn't find this creation",
    createYours: "Create Your Own",
    createYoursAlt: "Create Your Own",
    fallbackName: "A Rubby creation",
    seoDescription: "See what was created with Rubby: {name}",
    madeWith: "Made with Rubby",
    drawing: "Drawing",
    toy: "Toy",
    noAssets: "No assets available",
  },

  createFab: {
    start: "Start Creating!",
    startToy: "Start creating a toy",
  },

  nav: {
    bottom: "Bottom navigation",
    stepOf: "Step {current} of {total}",
    stepOfLabel: "Step {current} of {total}: {label}",
  },

  reorder: {
    preparing: "Preparing reorder...",
    failed: "Reorder failed",
  },

  birthdayClub: {
    title: "Birthday Club",
  },

  referral: {
    title: "Refer & Earn",
  },

  testimonialsUi: {
    fromReviews: "from {n} reviews",
    loadMore: "Load More Reviews",
  },

  confirmedTray: {
    removeCharacter: "Remove character",
  },

  specsUi: {
    perForever: "/ Forever",
    perMonth: "/month",
    chooseSize: "Choose Size",
    chooseMaterial: "Choose Material",
    chooseColor: "Choose Color Plan",
    smallLabel: "Small (10cm)",
    smallDesc: "Desk-sized, perfect for display",
    mediumLabel: "Medium (18cm)",
    mediumDesc: "Great for play and display",
    largeLabel: "Large (25cm)",
    largeDesc: "Huggable companion size",
    veryLargeLabel: "Very Large (35cm)",
    veryLargeDesc: "Statement piece",
    plaLabel: "Standard PLA",
    plaDesc: "Great detail, eco-friendly",
    petgLabel: "Tough PETG",
    petgDesc: "Extra durable for rough play",
    tpuLabel: "Flexible TPU",
    tpuDesc: "Soft and bendy, safe for toddlers",
    resinLabel: "High Detail Resin",
    resinDesc: "Museum-quality finish",
    singleLabel: "Single Color",
    singleDesc: "Clean, classic look",
    fullLabel: "Full Color",
    fullDesc: "Vibrant, true-to-drawing colors",
  },

  checkoutTrust: {
    moneyBack: "30-Day Money-Back",
    ssl: "SSL Secured",
    noHiddenFees: "No Hidden Fees",
    trackedShipping: "Tracked Shipping",
  },

  storyReader: {
    femaleVoices: "Female Voices",
    maleVoices: "Male Voices",
    recommendedShort: "Rec.",
    character: {
      Sulafat: "Warm",
      Vindemiatrix: "Gentle",
      Aoede: "Breezy",
      Leda: "Youthful",
      Despina: "Smooth",
      Achernar: "Soft",
      Achird: "Friendly",
      Puck: "Upbeat",
      Charon: "Informative",
      Sadachbia: "Lively",
      Enceladus: "Breathy",
    },
    bestFor: {
      Sulafat: "Bedtime stories, all ages",
      Vindemiatrix: "Toddler stories, soothing narration",
      Aoede: "Light adventure, fun stories",
      Leda: "Young-sounding, relatable for kids",
      Despina: "Elegant fairy tales",
      Achernar: "Quiet, lullaby-like narration",
      Achird: "Warm and approachable narrator",
      Puck: "Energetic, comedy, action stories",
      Charon: "Calm storyteller, educational",
      Sadachbia: "Animated, character-driven stories",
      Enceladus: "Soft, gentle male narrator",
    },
  },

  seo: {
    defaultTitle: "RUBBY — Toys, Storybooks & Animation from Kids' Art",
    defaultDescription: "Turn your child's drawing or photo into a custom 3D-printed toy, a personalised storybook, or an animated short film. UK-based, child-safe, delivered fast.",
    titleSuffix: "{title} | RUBBY",
    pricingTitle: "Pricing",
    pricingDesc: "See pricing for custom 3D-printed toys. Choose your package, size, material, and color plan.",
    trackOrderTitle: "Track Order",
    accountTitle: "My Account",
    faqTitle: "FAQ — Toys, Storybooks & Animation",
    faqDesc: "Answers about RUBBY's custom 3D-printed toys, personalised storybooks, animated films, credits, delivery, and safety.",
    reviewsTitle: "Reviews — RUBBY Toys, Storybooks & Animation",
    reviewsDesc: "Real stories from families who have made custom 3D-printed toys, personalised storybooks, and animated short films with RUBBY.",
    storybookTitle: "Create Your Storybook",
    signInTitle: "Sign In",
    privacyTitle: "Privacy Policy — RUBBY",
    privacyDesc: "How RUBBY protects your family's data across toys, storybooks, and animation. UK-GDPR and EU-GDPR compliant.",
    galleryTitle: "Gallery",
    galleryDesc: "Browse our gallery of custom 3D-printed toys created from real children's drawings.",
    animationTitle: "Create Your Animation",
    toyTitle: "Create Your Custom Toy",
    contactTitle: "Contact RUBBY",
    contactDesc: "Get in touch with the RUBBY team about toys, storybooks, animations, or your order. UK-based, we reply within 24 hours.",
    homeTitle: "Rubby — Home",
    qualityTitle: "Quality & Safety — Toys, Storybooks & Animation",
    qualityDesc: "Child-safe materials and quality processes for RUBBY toys, plus content-safety standards for storybooks and animation.",
    howItWorksTitle: "How RUBBY Works — Toys, Storybooks & Animation",
    howItWorksDesc: "See how RUBBY turns one drawing or photo into a 3D-printed toy, a personalised storybook, or an animated short film.",
    termsTitle: "Terms of Service — RUBBY",
    termsDesc: "RUBBY's terms for custom 3D-printed toys, personalised storybooks, animated films, credits, and subscriptions.",
    landingTitle: "Turn Kids' Drawings into Real 3D Toys",
    landingDesc: "Transform your child's artwork into a custom 3D-printed toy. AI-powered design, child-safe materials, delivered in 14 days.",
    aboutTitle: "About RUBBY — Toys, Storybooks & Animation from Kids' Art",
    aboutDesc: "Meet the team behind RUBBY. We turn children's drawings and photos into custom 3D-printed toys, personalised storybooks, and animated short films.",
  },

  errorBoundary: {
    sadFace: "Sad face",
    title: "Something went wrong",
    body: "An unexpected error occurred. You can try refreshing the page or going back to the home page.",
    details: "Error details",
    tryAgain: "Try Again",
    goHome: "Go to Home",
  },

  accountShipping: {
    comingSoon: "Coming soon",
    comingSoonDesc: "Default shipping address profiles are not available yet — there is no profiles endpoint for this.",
  },

  styleQuiz: {
    styles: {
      pixar3d: "Pixar 3D",
      realistic: "Realistic",
      chibi: "Chibi Figurine",
      brick: "Brick Figure",
      vinyl: "Designer Vinyl",
      bighead_vinyl: "Big-Head Vinyl",
      blindbox: "Blind-Box Vinyl",
      claymation: "Claymation",
      bobblehead: "Bobblehead",
    },
    sizes: {
      small: "Small",
      medium: "Medium",
      large: "Large",
      very_large: "Very Large",
    },
    materials: {
      standard_pla: "Standard PLA",
      tough_petg: "Tough PETG",
      flexible_tpu: "Flexible TPU",
      high_detail_resin: "High-Detail Resin",
    },
    colors: {
      single_color: "Single color",
      full_color: "Full color",
    },
    qChildAge: "How old is the child?",
    qColors: "What colors does the child love?",
    qPersonality: "What's their personality like?",
    age02: "0-2 years",
    age35: "3-5 years",
    age68: "6-8 years",
    age912: "9-12 years",
    age13: "13+ years",
    pastels: "Soft pastels",
    vibrant: "Bold & vibrant",
    neutral: "Natural tones",
    rainbow: "All the colors!",
    adventurous: "Adventurous explorer",
    sweet: "Sweet & cuddly",
    playful: "Playful & energetic",
    artistic: "Creative & artistic",
    resultTitle: "Here's what we recommend",
    quizTitle: "Smart Recommendation",
    basedOnAnswers: "Based on your answers",
    questionOf: "Question {current} of {total}",
    skip: "Skip, I'll choose myself",
    apply: "Apply to my toy",
    retake: "Retake quiz",
    colorPlan: "Color Plan",
    reasonTpuInfant: "Flexible TPU is soft and safe for teething and chewing.",
    reasonPlaPlay: "Standard PLA is durable and safe for active play.",
    reasonPlaDetail: "PLA offers great detail for this age group.",
    reasonPetgRough: "PETG is extra durable for older kids who play rough.",
    reasonSizeLargeHands: "Larger toys are safer and easier for small hands to hold.",
    reasonSizeMediumCuddle: "Medium is the perfect cuddle size for this age.",
    reasonSizeSmallShelf: "Compact size that fits on a shelf or in a backpack.",
    reasonStyleChibiAdventure: "Chibi figurine style matches their adventurous spirit!",
    reasonStylePixarAdventure: "Pixar 3D style brings their adventures to life.",
    reasonStylePixarSweet: "Soft Pixar 3D style is perfect for a sweet personality.",
    reasonStyleBrickPlayful: "Brick-figure style is fun and playful — just like them!",
    reasonStyleRealisticArtistic: "Realistic style showcases their artistic detail.",
    reasonStyleVinylArtistic: "Designer-vinyl style brings artistic flair to their creation.",
    reasonColorFull: "Full color brings out the vibrant palette they love.",
    reasonColorSingle: "Single color keeps it clean and elegant.",
  },
};



export type TranslationKeys = typeof en;

